/**
 * Live-translation DISPLAY state (LT-UI).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A SEPARATE SLICE AND NOT PART OF `transcriptionSlice`
 *
 * `transcriptionSlice.ts` is the SOURCE reducer. It keys on sync id / segment
 * id / speaker identity and falls back to text-and-timing similarity only to
 * recover legacy rows that carry no segment id. Feeding translated text through
 * it would put two different renderings of one utterance into a single keyspace
 * where that similarity fallback lives — a translation and its source would
 * collide as duplicates, and one of them would be dropped or would overwrite
 * the other. Widening those rules to tell them apart would be the wrong change:
 * they are correct for what they key.
 *
 * So translation lives here, keyed on the AUTHORITATIVE `source_turn_id`, and is
 * joined to the source through selectors. `transcriptionSlice` is not modified
 * by this task and none of its actions are handled here.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * RULE 1 — THE SOURCE IS PRIMARY AND IS NEVER REPLACED, STRUCTURALLY
 *
 * This slice stores NO source text. `translationSourceTurnObserved` validates a
 * whole `SourceTurnV1` against the frozen contract and then keeps only the
 * lineage fields; `text` is DROPPED on admission. The slice therefore has no
 * source text with which to displace a source line — a property of the data,
 * not of the render code. The test suite asserts it by searching the serialised
 * state for the source string.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE JOIN IS STATED, NEVER DERIVED
 *
 * A frontend transcript message carries `segmentId`, the Deepgram segment id
 * minted in `agent-backend/demo/handlers/transcription_handler.py` as
 * `f"{identity}-seg-{counter}"` — a PROCESS-LOCAL counter. The authoritative
 * `SourceTurnV1` carries the same value as `deepgram_segment_id` and derives
 * `source_turn_id = uuid5(ns, call_id|stable_participant_id|source_epoch|deepgram_segment_id)`.
 *
 * A bare segment id is NOT a key, and NO amount of bookkeeping on this side can
 * make it one. The app says so itself: `lib/transcripts/persistence.ts` builds
 * `segment-id:v1:<participant>:<segment>`, scoping the segment by participant.
 * A republished track bumps `source_epoch` while the counter can restart, so two
 * genuinely different source turns can carry one segment id, and the frontend
 * holds neither `stable_participant_id` (controller-minted, not the LiveKit
 * identity) nor `source_epoch` to tell them apart.
 *
 * An EARLIER version of this module joined whenever exactly one admitted turn
 * claimed a segment id, and refused only once a second turn claimed it. That is
 * wrong in the window that matters: after an epoch restart a NEW utterance
 * arrives while only the OLD turn claims that id, so "exactly one claimant"
 * displays the PREVIOUS utterance's translation under the new source line, and
 * only stops once the second ledger row happens to land. A displayed
 * mistranslation that later corrects itself is worse than no translation.
 *
 * So the join is now STATED by an authoritative correlation —
 * `translationTranscriptCorrelated` — keyed on the transcript message's own
 * `syncId`, and NOTHING is joined without one. `turnIdsBySegmentId` survives
 * only as telemetry: it reports that a segment id was reused, and is never read
 * to resolve a bubble.
 *
 * The consequence is deliberate and is the conservative direction: with no
 * correlations stated, every bubble reads `awaiting_lineage`. There is no
 * fallback heuristic — no timing window, no text similarity, no "most recent
 * wins", no language detection — anywhere in this module.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ONE CALL AT A TIME
 *
 * Segment ids are scoped to a call, so the index is only meaningful inside one
 * call. The slice holds exactly one `callId`; a record naming a different call
 * is REFUSED and counted. The first admitted record establishes the scope when
 * none is set.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * CONTENT, ROUTE CONDITION AND REVISION ARE ORTHOGONAL
 *
 * There is deliberately no single exclusive per-turn state. A turn that already
 * has a selected translation KEEPS SHOWING IT while its route is delayed,
 * reconnecting or unavailable, and while a sibling turn in the same bubble is in
 * any state at all. An exclusive state machine cannot represent that, and the
 * failure mode it produces — an operational blip erasing text the reader was
 * mid-sentence through — is the exact opposite of what the five states are for.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * REFUSALS ARE COUNTED, NEVER SILENT
 *
 * A reducer cannot throw without taking the store down, so an inadmissible
 * payload is refused and recorded in `rejected`. Nothing is coerced, defaulted,
 * trimmed to fit or repaired.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * RULE 4 — A REVISION CHANGES TEXT ONLY
 *
 * `heardRevisionId` records the revision whose AUDIO reached the listener.
 * `translationAudioDeliveryObserved` is its ONLY writer: appending a revision
 * and selecting a revision never touch it, so a later text revision cannot
 * retroactively claim to be what was played. Nothing here can ask for audio at
 * all — this module has no audio surface. Audibility belongs to the approved
 * audio router (`components/conference/audio-router/`), which this module
 * neither reads nor writes.
 */

import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  resolveCaptionRelation,
  type CaptionRelationResult,
} from '@/lib/translation/captionPolarity';

import {
  validateRouteEnvelopeV1,
  validateSourceTurnV1,
  type DeliveryState,
  type NuanceAnnotation,
  type MediaPurpose,
  type RecipientGroup,
  type RouteEnvelopeV1,
  type RouteState,
  type SourceTurnV1,
  type TranslationRevisionV1,
  type EngineIslandV1,
  validateEngineIslandV1,
} from '@/lib/translation/contracts';
import {
  isLaunchLanguageCode,
  languageEquals,
  type LaunchLanguageCode,
} from '@/lib/translation/launchLanguages';
import {
  composeMergedRecord,
  isTranslationOrigin,
  revisionAttachmentOf,
  translationOriginFromContract,
  validateTranslationRevisionRecord,
  type RevisionAttachmentV1,
  type TranslationOrigin,
  type TranslationRevisionRecordV1,
} from '@/lib/translation/revisionEnvelope';
import {
  captionEngineStreamKey,
  engineCoordinateSpaceKey,
  type SettledEngineRange,
} from '@/lib/translation/liveTranslatedCaptions';
import {
  isWorkerHealth,
  WORKER_HEALTH_FAIL_CLOSED,
  type WorkerHealth,
} from '@/lib/translation/workerHealth';
import {
  selectTranscriptionBubbles,
  type TranscriptionBubble,
  type TranscriptionMessage,
  type TranscriptionState,
} from './transcriptionSlice';

// ---------------------------------------------------------------------------
// display vocabulary
// ---------------------------------------------------------------------------

/**
 * What the translation CONTENT for a turn is, independent of how the transport
 * is behaving.
 *
 * `awaiting_lineage` is bubble-level only: it means the transcript bubble has no
 * admitted source turn to join to (or an ambiguous one), so nothing at all is
 * known about its translation yet. Every other member is turn-level.
 *
 * `source_is_target` mirrors `PAIRED_SOURCE_IS_TARGET` in the approved
 * `lib/translation/bilingualProjection.ts` rather than inventing a second name:
 * the turn was already spoken in the target language, so no translation is owed
 * and no translation line is rendered for it.
 */
export const TRANSLATION_CONTENT_STATUSES = [
  'source_is_target',
  'awaiting_lineage',
  'awaiting_translation',
  'unavailable',
  'translated',
] as const;
export type TranslationContentStatus = (typeof TRANSLATION_CONTENT_STATUSES)[number];

/**
 * How the route delivering this turn's translation is behaving. Orthogonal to
 * content: a turn can hold finished translated text while its route reconnects.
 *
 * `closed` is a normal end of call, not a fault. It only becomes a visible
 * `unavailable` for a turn whose translation never arrived — see
 * {@link translationTurnBadges}.
 */
export const TRANSLATION_ROUTE_CONDITIONS = [
  'ok',
  'delayed',
  'reconnecting',
  'unavailable',
  'closed',
] as const;
export type TranslationRouteCondition = (typeof TRANSLATION_ROUTE_CONDITIONS)[number];

/**
 * The five states the design requires to be explicit and to render distinctly.
 * They are BADGES, not an exclusive state: `revised` is independent of the
 * operational badge and can never be swallowed by it.
 */
export const TRANSLATION_BADGES = [
  'translating',
  'delayed',
  'reconnecting',
  'unavailable',
  'revised',
] as const;
export type TranslationBadge = (typeof TRANSLATION_BADGES)[number];

/**
 * Severity order for reducing several turns' operational badges to one, for a
 * bubble that spans several Deepgram segments. Highest first.
 *
 * `unavailable` outranks everything because it is the only terminal condition —
 * a translation that will never arrive must not be described as still arriving.
 * `revised` is NOT in this list: it is carried separately as
 * `revised_turn_count`, so a bubble that is part revised and part still in
 * flight reports both instead of losing one.
 */
const OPERATIONAL_BADGE_PRECEDENCE: readonly TranslationBadge[] = [
  'unavailable',
  'reconnecting',
  'delayed',
  'translating',
];

/** Observable route state -> how it reads to a viewer. */
const ROUTE_STATE_TO_CONDITION: Readonly<Record<RouteState, TranslationRouteCondition>> = {
  pending: 'ok',
  active: 'ok',
  translating: 'ok',
  draining: 'ok',
  delayed: 'delayed',
  reconnecting: 'reconnecting',
  unavailable: 'unavailable',
  ended: 'closed',
};

/** Purposes that can deliver translated speech to a listener. */
const LISTENER_PURPOSES = ['translated_source', 'recipient_mix'] as const;

/**
 * Route states that can still PRODUCE output.
 *
 * An ALLOW-LIST, deliberately. A state added to the route contract later is not
 * deliverable until someone decides it is; a deny-list would silently confirm a
 * state nobody has considered, which is the wrong direction for a predicate
 * whose false answer is "surface a failure".
 *
 * `draining` is a member: a draining session is still flushing queued output, so
 * it both can and does still deliver. `unavailable` is terminal and `ended` is
 * over, so neither can produce anything again.
 */
const DELIVERABLE_ROUTE_STATES: ReadonlySet<RouteState> = new Set<RouteState>([
  'pending',
  'active',
  'translating',
  'draining',
  'delayed',
  'reconnecting',
]);

/**
 * Route states in which delivery has actually BEGUN. Also an allow-list.
 *
 * `pending` is the whole reason this is a separate set from
 * {@link DELIVERABLE_ROUTE_STATES}: the route is assigned but has delivered
 * nothing. Treating it as established would announce a language as the one in
 * force before anything had been delivered in it — a request presented as an
 * accomplished fact.
 *
 * `delayed` and `reconnecting` ARE established: delivery began and is degraded,
 * which is a condition to report, not a reason to un-confirm the language.
 */
const ESTABLISHED_ROUTE_STATES: ReadonlySet<RouteState> = new Set<RouteState>([
  'active',
  'translating',
  'draining',
  'delayed',
  'reconnecting',
]);

// ---------------------------------------------------------------------------
// state
// ---------------------------------------------------------------------------

/** Lineage only. Deliberately NO `text` field — see the header. */
export interface TranslationSourceTurnRef {
  source_turn_id: string;
  call_id: string;
  deepgram_segment_id: string;
  stable_participant_id: string;
  source_epoch: number;
  /** Validated source lineage; older persisted stores may not retain it. */
  source_track_sid?: string;
  ordinal: number;
  started_at_ms: number;
  source_language: LaunchLanguageCode | null;
  /**
   * The per-utterance OBSERVATION (STT-detected), distinct from the
   * assignment-backed `source_language` claim. Null/absent from producers
   * older than the observation chain. Per the design review's polarity
   * contract, this OUTRANKS the roster/claim when present — a code-switching
   * speaker makes the claim wrong and this field right.
   */
  detected_language: LaunchLanguageCode | null;
  /**
   * Catalog A4: the highest `supersede_generation` observed for this turn (0
   * = never superseded). A supersede replaces the SOURCE text in place; the
   * engine's revisions for the pair stand (rule 91), so nothing else moves.
   */
  supersede_generation?: number;
}

export interface TranslationSelection {
  /** The revision currently rendered. */
  selected_revision_id: string;
  /** The FIRST revision ever selected for this pair. Kept for provenance. */
  first_selected_revision_id: string;
  /**
   * How many times the displayed revision has CHANGED. Monotonic, never reset.
   *
   * `revised` is `selection_change_count > 0`, not
   * `selected_revision_id !== first_selected_revision_id`. Comparing ids makes
   * the warning vanish on a rollback: select A, then B, then A again and the
   * reader has seen two different wordings, but an id comparison says nothing
   * ever changed. It also silently un-flags a pinned quote whose selection moved
   * after it was pinned. A warning that can disappear is not a warning.
   *
   * `revision_index > 0` would be wrong in the other direction: a turn whose
   * first and only selection is index 2 was never displayed differently.
   */
  selection_change_count: number;
  /**
   * The generation of the selection currently applied, monotonic per pair. The
   * D2 fence in `translationRevisionSelected` no-ops any selection whose
   * generation does not advance this, so a reordered or replayed live packet
   * cannot move the display backwards. `undefined` only for a pre-fence
   * selection dispatched without a generation (unit tests); every production
   * dispatch — the live revision handler and the hydration replay — carries one,
   * because the wire envelope (`RevisionSelectionV1`) requires it.
   */
  selection_generation?: number;
}

/**
 * A publication was OBSERVED in an established state, with everything the
 * addressing decision will later need.
 *
 * Recorded when it happens, not reconstructed afterwards. `routes` is keyed by
 * publication and holds only the LATEST envelope, so a route that established
 * and then ended has already overwritten its own history — a backfill that
 * scans current state can never recover it. The envelope's own recipient and
 * purpose fields are retained rather than a resolved boolean, so the addressing
 * rule is applied LATE, once the viewer is known, and is never skipped.
 */
export interface ObservedEstablishment {
  publication_sid: string;
  target_language: LaunchLanguageCode;
  /**
   * The route's assignment-backed SOURCE language at observation time, kept so
   * the quiet gate can tell a genuinely cross-language establishment from a
   * same-language passthrough (sol r57 finding 2: an en→en route must not
   * reveal the translation chrome — the owner's "99% of calls will be
   * english-english" case). Null when the envelope had not resolved it yet;
   * a later observation of the same publication backfills it (null → value
   * only, so the record stays accumulate-only).
   */
  source_language: LaunchLanguageCode | null;
  /**
   * WHEN this establishment was first observed, as a call-scoped monotonic
   * order — and when its route ENDED, if it has. The gate's human↔producer
   * pairing requires the two intervals to OVERLAP (sol r60 finding 1): the
   * latch deliberately retains a departed listener's mix, and without the
   * interval a later analysis-only producer could pair with that STALE
   * target and confirm a call where no person ever received cross-language
   * delivery. `ended_at_order` clears if the same publication re-establishes;
   * only state 'ended' closes an interval — 'unavailable' is a fault whose
   * delivery relationship persists (the marker must survive).
   */
  observed_at_order: number;
  ended_at_order: number | null;
  /**
   * STICKY: this cross-language producer was seen overlapping a matching
   * human delivery at some moment (sol r62 finding 1). Pairing is judged in
   * the REDUCER while both intervals are live and then latched here, because
   * a reconnect rewrites the human's interval — deriving the pair at select
   * time made previously-confirmed chrome vanish mid-call when the erased
   * interval no longer overlapped. Reset only with the map itself.
   */
  paired_confirming?: boolean;
  media_purpose: MediaPurpose;
  recipient_id: string | null;
  recipient_group: RecipientGroup | null;
  assignment_generation: number;
  /**
   * The TRANSLATED-TEXT language this viewer had selected WHEN THIS WAS
   * OBSERVED.
   *
   * Time-correlated on purpose. The observation is promoted later, so comparing
   * against whatever language the viewer has selected at PROMOTION time would
   * credit an establishment to a language they had not yet chosen.
   *
   * ⚠️ RENAMED from `viewer_listen_language`, which it never was. The value has
   * always been `state.targetLanguage` — the text selector that
   * `lib/translation/languageRequest.ts` states "has nothing to do with what
   * anybody HEARS". Now that {@link TranslationState.viewerListenLanguage}
   * holds a REAL durable listen language in the same reducer, the old name was
   * an active trap: comparing an establishment against the audio assignment
   * would refuse text for every viewer whose subtitles and audio differ, which
   * is a supported combination. Value and comparison are unchanged.
   */
  viewer_target_language: LaunchLanguageCode | null;
}

/**
 * A merged record as the projection RPC attests it (owner design 2026-09-02,
 * "One Transcript, Two Ears"; P3's additive `merged` array). Stored verbatim;
 * whether it is USED is decided at view time — see {@link mergedRecordFor}.
 */
export interface TranslationMergedRecordAttestation {
  /**
   * The translation the server paired — `null` for an unsettled turn. Kept so
   * the attestation can be matched against the revision actually selected.
   * ⚠️ NO source text and NO merged text are stored: this slice holds no
   * source text at all (its own invariant — "the source is primary and is
   * never replaced"; admission drops it). The server's `merged_text` is
   * VERIFIED on admission to be the shared composition of its own halves and
   * then discarded; the record on screen composes from the transcript's own
   * source text, so the two can never disagree.
   */
  translation_text: string | null;
  origin: TranslationOrigin | null;
  request_contract_version: string | null;
}

/** What a transient teardown preserves about a pair's selection history. */
export interface TranslationSelectionWatermark {
  /** Monotonic high-water mark of `selection_change_count`. */
  selection_change_count: number;
  /** The revision the reader was last shown, so a change across the boundary is visible. */
  last_selected_revision_id: string;
  /** The original first selection, so re-admission cannot re-seed it. */
  first_selected_revision_id: string;
  /**
   * The high-water selection generation, so the D2 fence survives a transient
   * teardown: after a remount there is no live selection, and without this a
   * stale live packet arriving before the next hydration could regress the
   * display. `undefined` only for a pre-fence selection (unit tests).
   */
  selection_generation?: number;
}

export type TranslationQuoteScope = 'bubble' | 'span';

export interface TranslationQuote {
  /** `PinnedItem.transcriptId` — already the stable identity for a pin. */
  quote_id: string;
  scope: TranslationQuoteScope;
  target_language: LaunchLanguageCode;
  /** One or MORE turns, in display order. A bubble pin can span several. */
  source_turn_ids: string[];
  /** Ids only, per turn, as at pin time. No text is ever copied into a quote. */
  pinned_revision_ids: Record<string, string | null>;
  /**
   * Each turn's monotonic selection-change count AT PIN TIME. `changed_since_pin`
   * compares counts, not ids, so a selection that moved away and came back is
   * still reported as moved.
   */
  pinned_selection_change_counts: Record<string, number>;
  /**
   * The correlations that resolved this quote's turns at pin time, REVALIDATED
   * every time the view is built.
   *
   * Storing turn ids alone was not enough: a correlation contradicted after the
   * pin makes its sync id permanently ambiguous, the bubble correctly falls back
   * — and the pinboard would go on presenting a translation whose lineage has
   * since been disproven. For an evidence surface that is the worst available
   * failure, so the quote carries what it relied on and checks it still holds.
   */
  pinned_lineage: TranscriptTurnCorrelation[];
}

/** The transcript tab's closed view-mode vocabulary (owner defect 5). */
export type TranscriptViewMode = 'my_language' | 'original' | 'original_with_translation';
export const TRANSCRIPT_VIEW_MODES: readonly TranscriptViewMode[] = [
  'my_language',
  'original',
  'original_with_translation',
];

/** One verified roster row. Languages are launch-catalog codes or null. */
/** One caller-surface translation notice (see `callerNoticesByCallId`). */
export interface CallerTranslationNoticeRecord {
  kind: 'on' | 'off';
  atMs: number;
  /** The caller's own language at the notice. */
  readerLanguage: LaunchLanguageCode | null;
  /** The other end's language (ON only; null for OFF). */
  otherLanguage: LaunchLanguageCode | null;
  /** TRUE when speech detection set the caller's language (server fact). */
  detected: boolean;
  /**
   * The roster generation the notice was decided on. A LATER notice needs a
   * newer roster: a roster that predates the ON must not read as "back to one
   * language" a moment later.
   */
  rosterGeneration: number;
}

export interface TranslationRosterParticipant {
  livekit_identity: string;
  recipient_id: string;
  spoken_language: LaunchLanguageCode | null;
  listen_language: LaunchLanguageCode | null;
  /**
   * WHO set the language — the server fact behind "you chose / we detected /
   * the officer set". Optional and advisory: absent on older responses.
   */
  assignment_source?: string | null;
}

export type TranslationRejectionReason =
  | 'contract_invalid'
  | 'call_scope_mismatch'
  | 'language_not_in_catalog'
  | 'ambiguous_segment_lineage'
  | 'ambiguous_transcript_correlation'
  | 'non_authoritative_transcript_identity'
  | 'unknown_source_turn'
  | 'unknown_revision'
  | 'revision_lineage_conflict'
  /** D4c (rule 121): a revision stacked on a pair whose latest row is final_attached. */
  | 'revision_after_settlement'
  | 'stale_route_generation'
  | 'stale_assignment_generation'
  | 'unknown_quote';

export interface TranslationRejectionLog {
  count: number;
  last_reason_code: TranslationRejectionReason | null;
  last_detail: string | null;
}

/**
 * The participant's own answer to "do I want to hear the original voice?".
 *
 * THREE values, and the third is not redundant. `null` is NOT "translated": it
 * means the participant has not been asked yet, and the mapping below sends it
 * to the same audio outcome as `'original'`. Collapsing it into a boolean would
 * throw away the one distinction the surface has to make — between a default
 * this code chose and a choice the person actually made — and on a live call
 * that distinction is the whole point.
 */
export type TranslationListenAudioChoice = 'original' | 'translated' | null;

export interface TranslationState {
  /** The viewer's chosen output language. `null` means the display is off. */
  targetLanguage: LaunchLanguageCode | null;
  /**
   * TRUE when `targetLanguage` was AUTO-RESOLVED (#55) rather than picked.
   *
   * Provenance for the field above, and classified WITH it: a preference that
   * survives a call boundary must carry its provenance across the same
   * boundary, or the next call's pill would present a resolution as a choice
   * the person made. Neither reset helper touches it, exactly as neither
   * touches `targetLanguage`. Any MANUAL selection — including Off — clears
   * it; only `translationTargetLanguageAutoResolved` sets it.
   */
  targetLanguageAuto: boolean;
  /**
   * The transcript tab's VIEW MODE (owner defect 5, 2026-08-24): "we should be
   * able to choose either language, transcript in original language, or one
   * that shows original with the transcription after it in brackets."
   *
   *   - 'my_language' (default): the reader-language translation is the sole
   *     primary line, exactly the #68 shape;
   *   - 'original': the source text alone;
   *   - 'original_with_translation': the source first, the reader-language
   *     text after it in brackets — a STRUCTURED pair, never interpolation.
   *
   * REPLACES the `showOriginal` boolean (design review T1). A PREFERENCE,
   * classified with `targetLanguage` and `listenAudioChoice`: it belongs to
   * the PERSON and survives every reset boundary.
   */
  transcriptViewMode: TranscriptViewMode;
  /**
   * The call the ONE "Live translation enabled" reveal toast has fired for.
   *
   * KEYED BY CALL AND RESET BY NEITHER HELPER (sol r58 finding 2): a plain
   * `revealAnnounced` boolean in the call-scoped bucket died with the state it
   * was guarding — `useTranslationCallScope`'s unmount cleanup dispatches
   * `clearTranslations()`, so a same-call Conference remount reset the latch
   * and re-announced on rehydration (the exact double-toast this exists to
   * prevent). This field SURVIVES every reset like a preference and
   * self-invalidates by comparison instead: "announced" means the CURRENT
   * `callId` is in the set, so a same-call remount stays silent while a
   * genuinely new call — a different id — is automatically re-armed. A SET,
   * not the last id (sol r59 finding 2): a single scalar forgot call A the
   * moment call B announced, so re-entering A announced it twice. Bounded by
   * the handful of calls one browser session visits. A mount that arrives
   * after an unannounced confirmation still announces: told once, not zero
   * times or twice.
   */
  announcedCallIds: Record<string, true>;
  /**
   * The CALLER surface's translation notices, per call, in order (N1/R12 and
   * the OFF mirror). Keyed by call and surviving both reset helpers for the
   * same reason as `announcedCallIds`: a remount or reconnect must not repeat a
   * notice, while a real CHANGE (on -> off -> on) is announced each time. Also
   * what the Transcript tab renders as its system rows. Bounded: only a change
   * of state appends, and one browser visits a handful of calls.
   */
  callerNoticesByCallId: Record<string, CallerTranslationNoticeRecord[]>;
  /**
   * The participant's hear-original choice. `null` until they choose.
   *
   * A PREFERENCE, classified with `targetLanguage` and `transcriptViewMode` and with
   * neither reset helper: it belongs to the PERSON, not to the call, so it
   * survives every reset boundary for exactly the reason the language does.
   *
   * ⚠️ It is NOT a routing input on its own. Exactly one site maps it into
   * `ListenerContext.preferOriginal` — {@link selectTranslationPreferOriginal} —
   * and that mapping fails to `true` for everything except an explicit
   * `'translated'`. A second mapping site would be a second place for the
   * default to drift away from "original".
   *
   * ⚠️ It is INDEPENDENT of worker health. `RoutePolicyEngine` reads
   * `preferOriginal || workerHealth !== 'healthy'`, so choosing translation can
   * never override a faulted worker — the disjunct still forces original. These
   * are two inputs to one expression, and coupling them here would let a
   * participant's preference silently defeat a fault signal.
   */
  listenAudioChoice: TranslationListenAudioChoice;
  /**
   * What the SERVER has acknowledged, and nothing else. `null` means no write
   * of this listener's has been acknowledged in THIS call.
   *
   * ⚠️ IT IS NOT A COPY OF {@link TranslationState.listenAudioChoice}. The two
   * are deliberately separate facts: the choice is what the participant ASKED
   * for, this is what the durable ledger CONFIRMED, and the gap between them is
   * the whole point. `selectTranslationPreferOriginal` permits translated audio
   * only where BOTH say `'translated'`, which is what makes the original→
   * translated direction non-optimistic.
   *
   * ⚠️ CALL-SCOPED, unlike the choice beside it. The ledger is keyed on
   * `(call_id, stable_participant_id)`, so an acknowledgement from a previous
   * call says nothing about this one; carrying it across would let translated
   * audio play in a new call under a confirmation that was never given for it.
   * The PREFERENCE survives the boundary and the ACKNOWLEDGEMENT does not, so a
   * participant who chose translation in call A re-requests it in call B and
   * hears the original until the server agrees again. That asymmetry is
   * intentional and it is the fail-safe direction.
   *
   * ⚠️ EXACTLY ONE ACTION SETS IT AND THREE WITHDRAW IT, and the asymmetry is
   * the design. `translationDeliveryIntentAcknowledged` is the only writer that
   * can make it non-null, and only on a durable 200. It is withdrawn to `null`
   * by `translationListenAudioChoiceSelected` when the choice MOVES (the
   * acknowledgement described a transition the participant has left), by
   * `translationDeliveryIntentFenceObserved` when the server names a generation
   * this browser did not produce (the row moved under it), and by
   * `resetCallScopedState` at a call boundary. Everything that can add doubt
   * clears it; nothing but the server can set it.
   */
  deliveryIntentAcknowledged: TranslationListenAudioChoice;
  /**
   * The acknowledged `delivery_intent_generation`, or `0` for "no row has been
   * acknowledged". `0` is exactly what the append RPC's
   * `p_expected_previous_generation` wants to hear for a first write, so this
   * field is sent verbatim rather than translated at the call site.
   *
   * MONOTONIC. The reducer refuses a generation that is not strictly greater
   * than the one it holds, so a response that overtook a newer one on the wire
   * cannot walk the fence backwards and re-open a window the listener already
   * moved past.
   */
  deliveryIntentGeneration: number;
  callId: string | null;
  /** This viewer's controller-minted `stable_participant_id`, for route scope. */
  viewerRecipientId: string | null;
  /**
   * The viewer's language-assignment generation (BI-11). A route minted before
   * it moved describes a world that no longer exists — a language the viewer
   * has since changed away from — so it must not drive the display even if its
   * `route_generation` is the highest. This mirrors the approved router's
   * `RoutePolicyEngine.ineligibleReason` and `RouteRegistry.staleReason`.
   */
  viewerAssignmentGeneration: number;
  /**
   * The viewer's language-assignment generation at the moment `targetLanguage`
   * was last requested. A request is ACKNOWLEDGED only once
   * `viewerAssignmentGeneration` moves past this.
   *
   * A route envelope carries no correlation back to the selection that caused
   * it, so the controller-owned assignment generation is the only fact that can
   * stop a retained route from a previous request confirming a later one
   * (es -> fr -> es). It is captured HERE, with the request, because a consumer
   * that captures it when IT mounts records the wrong moment: the usual mount is
   * after the pre-join request was already committed, so it would read the
   * CURRENT generation as the fence and compare `2 > 2`, leaving an
   * already-acknowledged request pending for ever.
   *
   * ⚠️ INVARIANT: this field is reset in exactly the places
   * `viewerAssignmentGeneration` is reset, and nowhere else. The two are only
   * meaningful relative to each other — a fence of 2 surviving into a call whose
   * generation restarts at 0 makes `0 > 2` false permanently, and nothing would
   * ever confirm again. Both live in `resetCallScopedState` for that reason, and
   * NEITHER is touched by `resetSessionScopedState`: a same-call remount must not
   * re-arm a fence against a generation that did not move.
   */
  /**
   * Whether validated viewer context has arrived in THIS call.
   *
   * No raw observation is judged until it has. Without this, a group-addressed
   * (`all_participants`) route satisfies the addressing check unconditionally
   * while the viewer generations are still their zero placeholders, so it is
   * promoted on observation and a later, higher-generation context can never
   * retract it — backfill only ever adds. The result is the false claim this
   * feature exists to prevent: a language reported as in force and then
   * interrupted, for someone it never reached.
   *
   * ⚠️ `viewerRecipientId !== null` CANNOT substitute. A group-addressed viewer
   * legitimately has a null recipient id, so that test cannot tell "no context
   * yet" from "context whose addressing is by group", and using it would refuse
   * every establishment such a viewer ever has.
   *
   * ⚠️ Retained across a session reset — a remount does not un-know who the
   * viewer is, and refusing to promote afterwards would strand every observation
   * that arrived post-remount. Cleared only by a call reset.
   */
  viewerContextKnown: boolean;
  /**
   * The viewer's DURABLE listen language, from
   * `call_participant_language_assignment_events` via the controller-owned
   * viewer snapshot. The one fact the audio router is blocked on.
   *
   * ⚠️ NOT {@link TranslationState.targetLanguage}. That is the translated-TEXT
   * selection and it is forbidden from deciding what anybody hears; the two are
   * independent and a viewer legitimately reads French subtitles while hearing
   * Japanese audio. Nothing in the browser derives this — not from a route
   * envelope, not from provider output, not from participant metadata, not from
   * the browser locale. `null` means "no assignment in force", which is a KNOWN
   * state, not an unknown one.
   */
  viewerListenLanguage: LaunchLanguageCode | null;
  /**
   * The controller's verdict on the translation worker, as of the last accepted
   * viewer snapshot AND still within that snapshot's local validity.
   *
   * `RoutePolicyEngine` reads it as `preferOriginal || workerHealth !== 'healthy'`
   * and demotes `translated_source` below `original_source` when that is true —
   * the §7.6 fallback that keeps the ORIGINAL audible through every degraded
   * mode. Until this field existed the runtime pinned the literal
   * `'unavailable'`, so the comparison had no producer and could not fire.
   *
   * ⚠️ IT EXPIRES, and the expiry is owned by `useTranslationViewerContext`
   * rather than by this reducer. A stale `healthy` is strictly worse than
   * `unavailable`: it re-opens the hole the literal was closing. The hook holds
   * the deadline because only the hook knows when its own request started, and
   * anchoring a server-supplied duration to a browser clock is the one thing
   * neither side may do to the other.
   *
   * ⚠️ NOT part of `translationViewerContextSet`. There are 88 construction
   * sites for that action across eight files; adding a required field would
   * touch every one, and an optional field is one a producer can forget — which
   * on this path means falling through to a default that decides what somebody
   * hears. It gets its own required action instead.
   */
  viewerWorkerHealth: WorkerHealth;
  /**
   * The SERVER-VERIFIED participant roster (own-language program C1): every
   * proof-verified `livekit_identity -> recipient_id` binding in the room,
   * with each participant's assigned languages. The caption polarity rule
   * (`same|different|unknown` against the viewer's own language) and, later,
   * the audio audible-set key on it — which is why NOTHING in the browser may
   * derive these bindings from participant attributes: the spid attribute is
   * self-writable, and only the server can check the HMAC proof.
   *
   * `present: false` is the KNOWN-empty state — the last accepted response
   * carried no verifiable roster, so no relation may be asserted. It is not a
   * loading placeholder: consumers must treat it as `unknown`, never as
   * "same language".
   *
   * `generation` is a FENCE (max assignment generation at the server read),
   * retained across clears exactly like `viewerAssignmentGeneration`: a clear
   * removes the relations but must not re-open the door to a superseded
   * roster. Zeroed only at a call boundary.
   */
  participantRoster: {
    present: boolean;
    participants: TranslationRosterParticipant[];
    generation: number;
    /**
     * Participants present in the room whose binding could NOT be verified —
     * the endpoint counts them (`roster_unverified_count`) precisely so a
     * partial roster cannot masquerade as "everyone". Meaningful only while
     * `present`; cleared to 0 with the relations (review r14, finding 2).
     */
    unverified_count: number;
  };
  /**
   * Every identity→relation binding the server EVER verified this call — the
   * INTERIM caption join (design review finding 4). Unlike
   * {@link participantRoster}, which mirrors the room's current membership and
   * clears when a response carries no roster, this map only ACCUMULATES: a
   * participant who disconnects keeps their interims classifiable, and a
   * clear round does not turn a known speaker back into an unknown one.
   *
   * A CONFLICTING rebind (same identity, different recipient_id) is REFUSED —
   * the first verified binding wins for the life of the call. LiveKit never
   * legitimately reuses an identity within one room session, so a conflict is
   * evidence of trouble, and overwriting would let the trouble reclassify
   * text already attributed. Languages for the SAME binding may update.
   *
   * Cleared ONLY at a call boundary.
   */
  rosterRetainedByIdentity: Record<string, TranslationRosterParticipant>;
  /**
   * The last ASSIGNED listen language the context ever stated this call —
   * the caption matrix's fallback memory (r4 finding 2). Context
   * invalidation clears `viewerListenLanguage` (the audio path must fail
   * closed), but the READER did not forget their language because a lease
   * expired: with the text display off, disarming the matrix in that window
   * would expose foreign captions. Cleared only at a call boundary.
   */
  captionListenLanguageMemory: LaunchLanguageCode | null;
  targetLanguageAssignmentFence: number;
  /**
   * Every publication OBSERVED in an established state this call, keyed by
   * `publication_sid`, with the fields the addressing rule needs.
   *
   * The RAW fact, recorded on the admitted path even when the viewer is not yet
   * known. `establishedLanguageGenerations` below is promoted from it once the
   * addressing and generation rules can be applied.
   *
   * ⚠️ Same reset ownership as the map it feeds: NOT cleared by
   * `resetSessionScopedState`. If a remount dropped it, any observation not yet
   * promoted — precisely the case where the viewer context had not arrived — is
   * lost for good, which is the defect this record exists to prevent. Terminal
   * reset only.
   */
  observedEstablishments: Record<string, ObservedEstablishment>;
  /** Call-scoped monotonic order for observation intervals (sol r60 f1). */
  observationOrder: number;
  establishedLanguageGenerations: Partial<Record<LaunchLanguageCode, number>>;
  /** Complete validated envelopes, keyed by `publication_sid`. */
  routes: Record<string, RouteEnvelopeV1>;
  turns: Record<string, TranslationSourceTurnRef>;
  /**
   * TELEMETRY ONLY. List-valued so a reused segment id is observable, but never
   * read to resolve a bubble — a process-local counter cannot be made into a
   * key by counting its claimants. See the header.
   */
  turnIdsBySegmentId: Record<string, string[]>;
  /**
   * The authoritative join: transcript `syncId` -> `source_turn_id`, stated by
   * the producer and never derived here.
   */
  sourceTurnIdByTranscriptSyncId: Record<string, string>;
  /**
   * Sync ids a producer has correlated to two different turns. STICKY: once a
   * key is known to be ambiguous it never becomes joinable again, so a
   * contradiction cannot resolve itself into a confident wrong answer.
   */
  ambiguousTranscriptSyncIds: Record<string, true>;
  /**
   * Keyed by {@link pairKey}. Append-only history, ordered by revision index.
   * A record is the frozen 19-key core or the 25-key attached shape
   * (20261164) — stored exactly as validated, never coerced either way.
   */
  revisions: Record<string, TranslationRevisionRecordV1[]>;
  /** Keyed by {@link pairKey}. */
  selections: Record<string, TranslationSelection>;
  /**
   * Keyed by {@link pairKey}. What survives a transient teardown so that a
   * selection change SPANNING the boundary is still detectable.
   *
   * A count alone is not enough, and that was a real defect: after a remount
   * there is no live selection to compare against, so showing A, remounting,
   * then showing B looked like no change at all — and `first_selected_revision_id`
   * was re-seeded to B, destroying the fallback comparison too. The slice came
   * to believe B was always the first thing shown. The watermark therefore
   * carries the last revision the reader was shown and the original first one.
   *
   * Destroyed only by a genuine new call.
   */
  selectionWatermarks: Record<string, TranslationSelectionWatermark>;
  /** Keyed by {@link pairKey}. Structural markers only, never inferred. */
  unavailable: Record<string, { reason_code: string }>;
  /**
   * ENGINE ISLANDS (D1b / TL-01, rule 120): engine words that attached to no
   * source turn, keyed by island id. Their own records — never a synthetic
   * turn; rendered as their own marked row, attributed to the speaker.
   */
  islands: Record<string, EngineIslandV1>;
  /** Keyed by {@link pairKey}. The revision the listener actually HEARD. */
  heardRevisionId: Record<string, string>;
  /**
   * Keyed by {@link pairKey}. The projection RPC's ATTESTED merged record for
   * a pair (P3, additive `merged` rows) — "<source_text> [<translation_text>]"
   * with the origin the server derived. Absent for a pair the RPC never
   * described; the view then composes the record locally from the turn and
   * the selected revision (`composeMergedRecord`). See {@link mergedRecordFor}.
   */
  mergedRecords: Record<string, TranslationMergedRecordAttestation>;
  quotes: Record<string, TranslationQuote>;
  rejected: TranslationRejectionLog;
}

const initialState: TranslationState = {
  targetLanguage: null,
  targetLanguageAuto: false,
  transcriptViewMode: 'my_language',
  announcedCallIds: {},
  callerNoticesByCallId: {},
  // NOT `'original'`. `null` is AUTO — follow the ledger's policy_default intent
  // (owner rule 2026-08-15: features default ON). Only an explicit `'original'`
  // opts out; see `selectTranslationPreferOriginal`.
  listenAudioChoice: null,
  // NOTHING is acknowledged until a server round-trip says so. This is the
  // operand that keeps `preferOriginal` true through every unknown.
  deliveryIntentAcknowledged: null,
  deliveryIntentGeneration: 0,
  callId: null,
  viewerRecipientId: null,
  viewerAssignmentGeneration: 0,
  viewerContextKnown: false,
  viewerListenLanguage: null,
  // The REFUSING value. Nothing in this system may default to `healthy`.
  viewerWorkerHealth: WORKER_HEALTH_FAIL_CLOSED,
  participantRoster: { present: false, participants: [], generation: 0, unverified_count: 0 },
  rosterRetainedByIdentity: {},
  captionListenLanguageMemory: null,
  targetLanguageAssignmentFence: 0,
  observedEstablishments: {},
  observationOrder: 0,
  establishedLanguageGenerations: {},
  routes: {},
  turns: {},
  turnIdsBySegmentId: {},
  sourceTurnIdByTranscriptSyncId: {},
  ambiguousTranscriptSyncIds: {},
  revisions: {},
  selections: {},
  selectionWatermarks: {},
  unavailable: {},
  islands: {},
  heardRevisionId: {},
  mergedRecords: {},
  quotes: {},
  rejected: { count: 0, last_reason_code: null, last_detail: null },
};

/**
 * Collision-free composite key.
 *
 * The separator is U+0000 and the language comes LAST — the same composition,
 * for the same reason, as `selectionKey` in the approved
 * `lib/translation/bilingualProjection.ts`: a language code has already been
 * checked against the closed catalog, so it cannot contain the separator, and
 * splitting at the final U+0000 recovers the pair whatever the turn id holds. A
 * printable separator would let an unusual id address another turn's pair. The
 * escape is written out rather than embedded literally so the byte stays visible
 * in review and this file stays greppable text.
 */
function pairKey(sourceTurnId: string, targetLanguage: LaunchLanguageCode): string {
  return `${sourceTurnId}\u0000${targetLanguage}`;
}

/**
 * Identity forms `transcriptSyncIdentity` DERIVES locally when the producer
 * supplied no `sync_id` (`lib/transcripts/persistence.ts:76`).
 *
 * None of them is unique across a track republication:
 *   `segment-id:v1:<participant>:<segment>`   — segment is a process-local counter
 *   `segment-key:v1:<participant>:<key>`      — key can be a speaker/time bucket
 *   `legacy:v1:<participant>:<time>:<index>`  — positional
 *
 * A derived identity therefore cannot carry a correlation, and this is the hole
 * the first attempt at the segment-id fix left open: the OLD turn's correlation
 * stays in the map, the NEW utterance derives the SAME identity, and it resolves
 * to the old turn before any second correlation arrives to mark the key
 * ambiguous. Only a producer-supplied `sync_id` — which the identity function
 * returns verbatim and which is unique across epochs — may be joined.
 */
const DERIVED_TRANSCRIPT_IDENTITY_PREFIXES = [
  'segment-id:v1:',
  'segment-key:v1:',
  'legacy:v1:',
] as const;

/**
 * True only for a producer-supplied transcript identity.
 *
 * This is a check on a STRUCTURAL FORMAT this application mints itself, not a
 * semantic reading of a string: the prefixes are exactly the three literals
 * `transcriptSyncIdentity` composes.
 */
export function isJoinableTranscriptIdentity(syncId: string | undefined | null): syncId is string {
  if (!syncId) return false;
  return !DERIVED_TRANSCRIPT_IDENTITY_PREFIXES.some((prefix) => syncId.startsWith(prefix));
}

/**
 * Record that a language has been established for this viewer, if this route
 * says so. Idempotent, and applies the identical eligibility rules on both the
 * admission path and the backfill so the two cannot drift.
 */
/**
 * One observation per (publication, assignment generation).
 *
 * The separator is U+0000, written as an escape so the byte stays visible in
 * review — the same collision-free composition used elsewhere in this file.
 */
function observationKey(route: RouteEnvelopeV1): string {
  return `${route.publication_sid}\u0000${route.assignment_generation}`;
}

/**
 * OBSERVE that a publication reached an established state. Records the raw fact
 * unconditionally — the viewer may not be known yet — then tries to promote it.
 */
function observeEstablishment(state: TranslationState, route: RouteEnvelopeV1): void {
  if (route.target_language === null) return;
  if (!(LISTENER_PURPOSES as readonly string[]).includes(route.media_purpose)) return;
  if (!ESTABLISHED_ROUTE_STATES.has(route.state)) return;

  // Keyed by publication AND the assignment generation, so a republication at a
  // NEW generation is retained beside the one it supersedes rather than
  // replacing it — replacing left a stale record that was then rejected by the
  // very fence it should have satisfied. Retaining every distinct pair cannot
  // lose one.
  const key = observationKey(route);
  if (state.observedEstablishments[key] === undefined) {
    // A NEW assignment generation of a publication supersedes the old one's
    // interval (sol r61 finding 1): the prior generation stops describing a
    // live delivery the moment its successor establishes.
    for (const record of Object.values(state.observedEstablishments)) {
      if (
        record.publication_sid === route.publication_sid &&
        record.assignment_generation < route.assignment_generation &&
        record.ended_at_order === null
      ) {
        state.observationOrder += 1;
        record.ended_at_order = state.observationOrder;
      }
    }
    state.observationOrder += 1;
    state.observedEstablishments[key] = {
      publication_sid: route.publication_sid,
      target_language: route.target_language,
      source_language: route.source_language,
      media_purpose: route.media_purpose,
      recipient_id: route.recipient_id,
      recipient_group: route.recipient_group,
      assignment_generation: route.assignment_generation,
      viewer_target_language: state.targetLanguage,
      observed_at_order: state.observationOrder,
      ended_at_order: null,
    };
  } else if (state.observedEstablishments[key].ended_at_order !== null) {
    // The same publication re-established after ending: a FRESH interval, not
    // a resurrection of the old one (sol r61 finding 1 — clearing the end
    // while keeping the original start made the interval [start, ∞] and
    // falsely overlapped producers that lived entirely inside the gap).
    state.observationOrder += 1;
    state.observedEstablishments[key].observed_at_order = state.observationOrder;
    state.observedEstablishments[key].ended_at_order = null;
  }
  if (
    state.observedEstablishments[key].source_language === null &&
    route.source_language !== null
  ) {
    // A publication's source language can resolve AFTER its first established
    // observation. Backfilling null → value keeps the record accumulate-only
    // while letting the cross-language gate confirm a call whose source
    // arrived on a later envelope of the same publication.
    state.observedEstablishments[key].source_language = route.source_language;
  }
  promoteObservedEstablishment(state, state.observedEstablishments[key]);
}

/**
 * Apply the addressing and generation rules to a retained observation and, if
 * they hold, record the language against the generation it was established at.
 *
 * Recording the raw fact early must never become a way to skip these checks
 * late: an unaddressed establishment in the map would make the UI report that a
 * language was in force and stopped, for someone who never heard it.
 */
function promoteObservedEstablishment(
  state: TranslationState,
  record: ObservedEstablishment,
): void {
  // RECORD EARLY, JUDGE LATE — and "late" means after the viewer context is
  // known, not merely in a later function. Judging against the zero placeholder
  // generations is not judging at all.
  if (!viewerContextIsEffective(state)) return;
  if (!(LISTENER_PURPOSES as readonly string[]).includes(record.media_purpose)) return;
  if (!addressesViewer(state, record)) return;
  if (!isGenerationCurrent(state, record)) return;
  // The same exact-equality rule the audio policy applies, against the language
  // the viewer was listening to AT OBSERVATION TIME. `languageEquals` is false
  // when either side is null, so "not yet chosen" never satisfies it.
  if (!languageEquals(record.target_language, record.viewer_target_language)) return;
  const seen = state.establishedLanguageGenerations[record.target_language];
  if (seen === undefined || record.assignment_generation > seen) {
    state.establishedLanguageGenerations[record.target_language] = record.assignment_generation;
  }
}

function isSafeGeneration(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/**
 * The viewer context is KNOWN, so this viewer's eligibility can be judged at all.
 *
 * Zero placeholders are not a context: before the controller has spoken, a
 * group-addressed route satisfies the addressing check unconditionally while the
 * generation fence is still at its zero default, and promoting it would report a
 * language as in force for somebody it never reached.
 *
 * ⚠️ This gates PROMOTION and SELECTION — deciding what may become or remain
 * governing. It never RETRACTS an entry from `establishedLanguageGenerations`,
 * which is history and only ever rises.
 */
function viewerContextIsEffective(translation: TranslationState): boolean {
  return translation.viewerContextKnown;
}

function refuse(state: TranslationState, reason: TranslationRejectionReason, detail: string): void {
  state.rejected.count += 1;
  state.rejected.last_reason_code = reason;
  state.rejected.last_detail = detail;
}

function describeErrors(errors: readonly { instancePath: string; keyword: string }[]): string {
  return errors.map((error) => `${error.instancePath || '<root>'} ${error.keyword}`).join(', ');
}

/**
 * Admit a record into the call scope, or refuse it.
 *
 * The first admitted record establishes the scope when none is set; after that a
 * record naming a different call is refused. Segment ids are scoped to a call, so
 * a cross-call record would corrupt the lineage index rather than merely add a
 * row nobody reads.
 */
function admitToCallScope(state: TranslationState, callId: string, detail: string): boolean {
  if (state.callId === null) {
    state.callId = callId;
    return true;
  }
  if (state.callId !== callId) {
    refuse(state, 'call_scope_mismatch', `${detail} (scope ${state.callId}, record ${callId})`);
    return false;
  }
  return true;
}

/**
 * Drop the volatile per-call working set.
 *
 * TRANSIENT: this is an unmount or a re-admission WITHIN one call. The monotonic
 * `selectionWatermarks` and the pinned `quotes` deliberately survive, because
 * neither describes transport state — they describe what the reader has already
 * been shown and what they chose to keep. Recreating them at zero would silently
 * withdraw a revision warning and a quote's movement baseline.
 */
function resetSessionScopedState(state: TranslationState): void {
  state.routes = {};
  state.turns = {};
  state.turnIdsBySegmentId = {};
  state.sourceTurnIdByTranscriptSyncId = {};
  // `ambiguousTranscriptSyncIds` is deliberately NOT cleared here. A producer
  // contradicting itself is a permanent fact about this CALL, and dropping it on
  // a remount would launder it: the disproven correlation could be re-stated and
  // become authoritative again, and a pinned quote would resume showing a
  // translation that had already been contradicted. It is destroyed only by a
  // new call, alongside everything else call-owned.
  state.revisions = {};
  state.selections = {};
  state.unavailable = {};
  state.islands = {};
  // An attestation describes a revision; it goes with the revisions.
  state.mergedRecords = {};
  // `heardRevisionId` is deliberately NOT cleared here. It records what the
  // listener ACTUALLY HEARD — an irreversible historical fact whose only writer
  // is the audio-delivery event. A remount does not un-hear it, and re-admitting
  // revisions can never reconstruct it, so dropping it here would silently
  // discard the very fact rule 4 rests on: that a later text revision cannot
  // change what was played. It is call-owned, and dies only with the call.
}

/**
 * Destroy EVERYTHING call-scoped, watermarks and quotes included.
 *
 * A different call must inherit nothing: a previous call's route must never be
 * able to confirm a new selection, and a previous call's quote must never render
 * against new lineage. This is the boundary that stays hard.
 */
function resetCallScopedState(state: TranslationState): void {
  resetSessionScopedState(state);
  state.ambiguousTranscriptSyncIds = {};
  state.heardRevisionId = {};
  state.selectionWatermarks = {};
  state.quotes = {};
  state.observedEstablishments = {};
  state.observationOrder = 0;
  state.establishedLanguageGenerations = {};
  // The viewer's identity and generations are CALL-OWNED, not preferences. They
  // decide whether a route is addressed to this viewer and whether it is
  // generation-current, so carrying them into a different call would make the
  // new call's routes eligible against the previous call's context before any
  // new context has arrived. Exactly three PREFERENCES survive a call boundary —
  // `targetLanguage`, `transcriptViewMode` and `listenAudioChoice` — because each belongs
  // to the PERSON rather than to the call. None of them is written here or in
  // `resetSessionScopedState`, and that absence is what makes them survive.
  state.viewerRecipientId = null;
  state.viewerContextKnown = false;
  state.viewerAssignmentGeneration = 0;
  // The roster is call-owned with its fence: generations restart per call, so
  // a surviving fence of 5 would refuse the new call's legitimate roster
  // for ever — the same arithmetic that keeps `targetLanguageAssignmentFence`
  // reset in lockstep with the generation above.
  state.participantRoster = {
    present: false,
    participants: [],
    generation: 0,
    unverified_count: 0,
  };
  // The retained identity map is the one roster structure that SURVIVES roster
  // clears — but never a call: call B's identities are new mints, and call A's
  // bindings assert nothing about them.
  state.rosterRetainedByIdentity = {};
  state.captionListenLanguageMemory = null;
  // ⚠️ THE ACKNOWLEDGEMENT IS CALL-OWNED EVEN THOUGH THE CHOICE IS NOT, and the
  // pair straddling this boundary is deliberate. `call_participant_translation_
  // delivery_intent_events` is keyed on `(call_id, stable_participant_id)`, so
  // call A's generation 3 is not call B's anything — sending it as call B's
  // `expected_previous_generation` would be a stale-fence 409 at best, and
  // believing call A's `'translated'` in call B would make translated audio
  // audible under a confirmation nobody gave for that call at worst. Absence is
  // the fail-safe here exactly as it is in the ledger: no acknowledged row means
  // original.
  state.deliveryIntentAcknowledged = null;
  state.deliveryIntentGeneration = 0;
  // The listen language is call-owned for exactly the reason the generation is:
  // a previous call's listen language would make the NEW call's routes audible
  // in a language nobody has been assigned yet. It must die with the call, and
  // it must SURVIVE a session reset for the same reason `viewerContextKnown`
  // does — a remount does not un-know who the viewer is.
  state.viewerListenLanguage = null;
  // Health is call-owned too, and for the sharpest version of the same reason:
  // a previous call's `healthy` would let the NEW call's translated routes win
  // over the original before anything has observed the new call's coordinator at
  // all. It is deliberately NOT in `resetSessionScopedState` — a remount inside
  // one call has not re-observed the worker either way, and dropping the verdict
  // there would force the original for a whole lease window for no reason.
  state.viewerWorkerHealth = WORKER_HEALTH_FAIL_CLOSED;
  // Moves with `viewerAssignmentGeneration`, always. See the field's comment:
  // resetting one without the other makes the acknowledgement comparison
  // meaningless, and silently so.
  state.targetLanguageAssignmentFence = 0;
  state.rejected = { count: 0, last_reason_code: null, last_detail: null };
}

const translationSlice = createSlice({
  name: 'translation',
  initialState,
  reducers: {
    /** `null` turns the bilingual display off. A non-catalog code is refused. */
    translationTargetLanguageSelected: (
      state,
      action: PayloadAction<LaunchLanguageCode | null>,
    ) => {
      const next = action.payload;
      if (next !== null && !isLaunchLanguageCode(next)) {
        refuse(state, 'language_not_in_catalog', `target language ${JSON.stringify(next)}`);
        return;
      }
      state.targetLanguage = next;
      // A manual selection — including an explicit Off — ends auto. #55's one
      // hard rule runs through here: auto may never resurrect captions over a
      // choice the person actually made.
      state.targetLanguageAuto = false;
      // Captured atomically with the request. A refused request above returns
      // before this line, so it cannot re-arm the previous request's fence
      // against a newer generation.
      state.targetLanguageAssignmentFence = state.viewerAssignmentGeneration;
    },

    /**
     * The #55 AUTO resolution — the same commit as a selection in every way
     * that matters to routing (same fence capture, same catalog refusal), but
     * marked as auto so no surface presents it as the participant's choice.
     */
    translationTargetLanguageAutoResolved: (
      state,
      action: PayloadAction<LaunchLanguageCode | null>,
    ) => {
      const next = action.payload;
      if (next !== null && !isLaunchLanguageCode(next)) {
        refuse(state, 'language_not_in_catalog', `auto target language ${JSON.stringify(next)}`);
        return;
      }
      state.targetLanguage = next;
      state.targetLanguageAuto = true;
      state.targetLanguageAssignmentFence = state.viewerAssignmentGeneration;
    },

    /**
     * The transcript tab's view mode (owner defect 5). Preference only: no
     * mode deletes or alters a revision — they choose what is PRIMARY.
     * VALIDATED against the closed vocabulary; junk is refused, not coerced,
     * because a mode this reducer invented would decide what somebody reads.
     */
    translationTranscriptViewModeSet: (state, action: PayloadAction<TranscriptViewMode>) => {
      if (!TRANSCRIPT_VIEW_MODES.includes(action.payload)) {
        refuse(state, 'contract_invalid', `transcript view mode ${JSON.stringify(action.payload)}`);
        return;
      }
      state.transcriptViewMode = action.payload;
    },

    /**
     * The reveal toast FIRED for the current call. Dispatching twice is a
     * no-op; a different call re-arms by comparison, not by reset (the field
     * deliberately survives both reset helpers — see its declaration).
     */
    translationRevealAnnounced: (state) => {
      if (state.callId !== null) state.announcedCallIds[state.callId] = true;
    },

    /**
     * Record a caller-surface notice for the current call — only as a CHANGE:
     * the first must be 'on', and each later one must differ from the last, so
     * a remount, a reconnect or a double dispatch can never repeat one.
     * Malformed input is refused, not coerced.
     */
    callerTranslationNoticeRecorded: (
      state,
      action: PayloadAction<CallerTranslationNoticeRecord>,
    ) => {
      const notice = action.payload;
      if (state.callId === null) return;
      const valid =
        (notice.kind === 'on' || notice.kind === 'off') &&
        typeof notice.atMs === 'number' &&
        Number.isFinite(notice.atMs) &&
        (notice.readerLanguage === null || isLaunchLanguageCode(notice.readerLanguage)) &&
        (notice.otherLanguage === null || isLaunchLanguageCode(notice.otherLanguage)) &&
        typeof notice.detected === 'boolean' &&
        isSafeGeneration(notice.rosterGeneration);
      if (!valid) {
        refuse(state, 'contract_invalid', `caller notice ${JSON.stringify(notice)}`);
        return;
      }
      if (!state.callerNoticesByCallId) state.callerNoticesByCallId = {};
      const list = state.callerNoticesByCallId[state.callId] ?? [];
      const last = list[list.length - 1];
      if (last ? last.kind === notice.kind : notice.kind !== 'on') return;
      state.callerNoticesByCallId[state.callId] = [...list, { ...notice }];
    },

    /**
     * The participant's hear-original choice. `null` withdraws it back to the
     * default; anything else is refused and the field is left where it was.
     *
     * VALIDATED POSITIVELY, and the refusal is the FALL-THROUGH. The check
     * enumerates the three admissible values and assigns inside that branch, so
     * a value this code has never heard of cannot reach the assignment — where
     * a `!==`-shaped guard would have to be extended by hand for every value
     * added later, and would silently admit anything a future producer invents.
     * The payload is widened to `unknown` first so TypeScript narrows on the
     * comparisons themselves rather than trusting the declared type: a value
     * arriving over a boundary the compiler never saw is exactly the case this
     * guard exists for.
     *
     * ──────────────────────────────────────────────────────────────────────
     * ⚠️ A CHANGE OF MIND WITHDRAWS THE ACKNOWLEDGEMENT. THIS IS THE SAFETY
     *    HALF OF THIS REDUCER, NOT A TIDY-UP.
     *
     * `selectTranslationPreferOriginal` permits translated audio only where the
     * choice AND the acknowledgement both say `'translated'`, and its entire
     * value is that the second one cannot be true without a durable server
     * write. That held only while the acknowledgement described the transition
     * the participant is CURRENTLY in — and nothing used to end it. So:
     *
     *   acknowledged 'translated'  ->  choose 'original'  (the write commits
     *   server-side but its response is lost, or it is refused 409 because
     *   another writer advanced the row)  ->  choose 'translated' again
     *
     * left BOTH conjuncts satisfied instantly, from facts belonging to two
     * different worlds, with no server agreement of any kind since — while the
     * durable ledger quite possibly said `'original'`. That is exactly the
     * optimistic behaviour the asymmetry exists to forbid, and with the 409 it
     * was worse: the send loop had latched and no new write was even attempted.
     *
     * So a choice that MOVES withdraws the acknowledgement. Re-pressing the
     * option already in force does not — that is not a new transition, and
     * clearing there would demote audio a listener is legitimately hearing and
     * burn a generation on every press.
     *
     * ⚠️ THE GENERATION IS RETAINED. It is the compare-and-set fence, not a
     * claim about the intent, and only the intent was superseded. Clearing it
     * would make the next write send `expected_previous_generation: 0` against
     * a ledger already at 3 — a 409 for ever, which is the same stranding this
     * change exists to end.
     *
     * ⚠️ IT LIVES HERE AND NOT IN THE NETWORK LAYER on purpose. Every
     * dispatcher of the choice gets the invariant, so there is ONE expression
     * to get right rather than a fail-safe reimplemented beside each send. A
     * REFUSED value withdraws nothing: a refusal is not a transition, and
     * demoting a listener's audio for a dispatch the reducer would not even
     * store is the wrong direction to fail in.
     */
    translationListenAudioChoiceSelected: (
      state,
      action: PayloadAction<TranslationListenAudioChoice>,
    ) => {
      const next = action.payload as unknown;
      if (next === null || next === 'original' || next === 'translated') {
        if (next !== state.listenAudioChoice) state.deliveryIntentAcknowledged = null;
        state.listenAudioChoice = next;
        return;
      }
      refuse(state, 'contract_invalid', `listen audio choice ${JSON.stringify(next)}`);
    },

    /**
     * The SERVER's acknowledgement of this listener's delivery intent — the
     * only action that can make
     * {@link TranslationState.deliveryIntentAcknowledged} non-null. (It said
     * "the only writer" until the stale-acknowledgement fix; three actions now
     * WITHDRAW it, and none of them can set one. See the field's own comment.)
     *
     * Dispatched from exactly one place, on a 200 from
     * `POST /api/calls/[callId]/translation/delivery-intent`. There is no action
     * that sets the acknowledgement optimistically, and there must never be one:
     * the field's entire value is that it cannot be true unless a durable write
     * happened.
     *
     * THREE REFUSALS, and each closes a real reordering:
     *
     *  1. SCOPE. An acknowledgement for a different call is refused, exactly as
     *     the viewer-context actions are. A response that lands after the
     *     participant has moved to another call must not confirm anything in it.
     *  2. MONOTONICITY. A generation that is not strictly greater than the one
     *     held is refused. Two writes in flight can settle out of order, and
     *     applying the older one would walk the fence backwards — the next
     *     request would then send a stale `expected_previous_generation` and
     *     409 against a row this browser itself wrote.
     *  3. VOCABULARY. The intent is validated positively against the closed set,
     *     with the refusal as the FALL-THROUGH, for the same reason the choice
     *     reducer above is written that way: a value nobody taught this code
     *     about cannot reach the assignment.
     *
     * ⚠️ A REFUSAL LEAVES THE FIELD WHERE IT WAS, which is the safe direction
     * everywhere: the worst case is that translated audio is not enabled, never
     * that it is enabled on a fact this reducer did not accept.
     */
    translationDeliveryIntentAcknowledged: (
      state,
      action: PayloadAction<{
        call_id: string;
        delivery_intent: TranslationListenAudioChoice;
        delivery_intent_generation: number;
      }>,
    ) => {
      const { call_id: callId, delivery_intent: intent } = action.payload;
      const generation = action.payload.delivery_intent_generation as unknown;

      if (state.callId === null || callId !== state.callId) {
        refuse(
          state,
          'call_scope_mismatch',
          `delivery intent ack for call ${JSON.stringify(callId)}`,
        );
        return;
      }
      if (
        typeof generation !== 'number' ||
        !Number.isSafeInteger(generation) ||
        generation <= state.deliveryIntentGeneration
      ) {
        refuse(
          state,
          'contract_invalid',
          `delivery intent generation ${JSON.stringify(generation)} does not advance ${state.deliveryIntentGeneration}`,
        );
        return;
      }
      const next = intent as unknown;
      if (next !== 'original' && next !== 'translated') {
        refuse(state, 'contract_invalid', `delivery intent ack ${JSON.stringify(next)}`);
        return;
      }

      state.deliveryIntentAcknowledged = next;
      state.deliveryIntentGeneration = generation;
    },

    /**
     * A generation the SERVER named while REFUSING this browser's write.
     *
     * WHY THIS EXISTS. A 409 means the compare-and-set fence this browser sent
     * was stale. Until now the only fact it could learn was its own — "did a
     * concurrent response of mine advance the generation while that request was
     * in flight?" — and when the answer was no, the loop stopped for the rest of
     * the lineage. The listener was then silently stranded: their choice was
     * never persisted, the mixer was never re-instructed, and the only thing the
     * surface said was that the router was not reflecting them. A remount
     * cleared the stop but not the ignorance, so the very next write conflicted
     * again.
     *
     * The endpoint DOES know the fence it refused against — the RPC's own 55000
     * names it — so it now reports it, and this is where that number lands. One
     * honest reconciliation against a generation the SERVER issued, in place of
     * the blind `expected + 1, + 2 …` probe the send loop has always refused to
     * perform.
     *
     * ⚠️ IT WRITES THE FENCE AND WITHDRAWS THE ACKNOWLEDGEMENT. IT NEVER SETS
     * ONE. Those are two different facts and only one of them arrived:
     *
     *  - the GENERATION is what the next write must send, so it is taken.
     *  - the INTENT did not arrive and must not be guessed. The endpoint cannot
     *    read it — the events table is `REVOKE ALL … FROM service_role` under
     *    FORCE ROW LEVEL SECURITY with no policies, so the only ledger fact on
     *    this path is the number in the refusal. A second writer of
     *    `deliveryIntentAcknowledged` would destroy the one property that field
     *    has: that nothing but a durable 200 can set it.
     *  - and the acknowledgement this browser HELD is now known to be stale. A
     *    generation it did not produce means the row moved under it, so what it
     *    believes the row SAYS is no longer current. Withdrawing is the
     *    fail-safe direction and it is also the honest one — and it is what
     *    stops another writer's `'original'` from leaving this listener on
     *    translated audio.
     *
     * The same three refusals as the acknowledgement above, for the same
     * reasons: SCOPE, because a conflict reported for another call says nothing
     * about this one; MONOTONICITY, because a fence may only move forward — the
     * ledger is append-only and a lower number is a response that overtook a
     * newer one; and a non-integer is refused rather than coerced. A refusal
     * leaves BOTH fields exactly where they were.
     */
    translationDeliveryIntentFenceObserved: (
      state,
      action: PayloadAction<{ call_id: string; delivery_intent_generation: number }>,
    ) => {
      const { call_id: callId } = action.payload;
      const generation = action.payload.delivery_intent_generation as unknown;

      if (state.callId === null || callId !== state.callId) {
        refuse(
          state,
          'call_scope_mismatch',
          `delivery intent fence for call ${JSON.stringify(callId)}`,
        );
        return;
      }
      if (
        typeof generation !== 'number' ||
        !Number.isSafeInteger(generation) ||
        generation <= state.deliveryIntentGeneration
      ) {
        refuse(
          state,
          'contract_invalid',
          `delivery intent fence ${JSON.stringify(generation)} does not advance ${state.deliveryIntentGeneration}`,
        );
        return;
      }

      state.deliveryIntentGeneration = generation;
      state.deliveryIntentAcknowledged = null;
    },

    /** Explicit scope change. Everything call-scoped is dropped on a new call. */
    translationCallScopeSet: (state, action: PayloadAction<string | null>) => {
      if (state.callId === action.payload) return;
      // Captured BEFORE the reset, which clears the buffer along with everything
      // else call-owned.
      state.callId = action.payload;
      resetCallScopedState(state);
    },

    /**
     * Everything about THIS viewer that decides which routes may drive the
     * display: their recipient key and their assignment generation. Mirrors the
     * approved router's `ListenerContext` rather than inventing a second shape
     * for the same facts.
     */
    translationViewerContextSet: (
      state,
      action: PayloadAction<{
        /**
         * The call this context describes. REQUIRED, like every other record the
         * slice admits — without it a delayed snapshot from a previous call is
         * indistinguishable from this call's own.
         */
        call_id: string;
        recipient_id: string | null;
        /**
         * The viewer's DURABLE listen language, or `null` for "no assignment in
         * force". REQUIRED, not optional: an optional field is one a producer
         * can forget, and the audio path must never be able to fall through to a
         * default it did not mean.
         */
        listen_language: LaunchLanguageCode | null;
        assignment_generation: number;
      }>,
    ) => {
      const { call_id, recipient_id, assignment_generation } = action.payload;
      const { listen_language } = action.payload;
      if (!isSafeGeneration(assignment_generation)) {
        refuse(
          state,
          'contract_invalid',
          `viewer generations ${JSON.stringify([assignment_generation])}`,
        );
        return;
      }
      // THE WHOLE UPDATE, or none of it. A snapshot with a malformed language is
      // not "mostly right" — it is a context whose audio half cannot be judged.
      // `null` is a VALID language: it means no assignment, which is a known
      // state and fails closed at the selector rather than here.
      if (listen_language !== null && !isLaunchLanguageCode(listen_language)) {
        refuse(
          state,
          'contract_invalid',
          `viewer listen language ${JSON.stringify(listen_language)}`,
        );
        return;
      }
      // SCOPE FIRST — before the first-context exception below, and before any
      // mutation or backfill.
      //
      // WHAT THIS FIXES: a new call resets the context, so a delayed
      // PREVIOUS-call snapshot has nothing to be compared against and the
      // first-context exception admits it as the new baseline. The monotonic
      // guard then refuses this call's legitimate lower generations as a
      // regression, stranding the viewer on the old call's identity for the rest
      // of this one. Before the guard existed the stale value was
      // self-correcting; the guard made it permanent.
      //
      // ON THE POSITION: keeping this ahead of the guard is deliberate, but I
      // could NOT construct a case where moving it below changes the outcome —
      // `translationCallScopeSet` has already set `callId`, so the mismatch is
      // caught wherever the check sits. It is defence in depth against a future
      // path that leaves `callId` null, not a load-bearing ordering. Said plainly
      // so nobody later "proves" an ordering claim that the tests do not make.

      if (!admitToCallScope(state, call_id, 'viewer context')) return;

      // MONOTONIC, once a context exists. A context snapshot that arrives late
      // must not rewind the viewer's generation: doing so promotes observations
      // the current generation had already ruled stale, and because backfill
      // only ever ADDS, restoring the true generation cannot retract them. The
      // whole update is refused — no half-applied context and no backfill —
      // because a partially accepted regression is still a regression.
      //
      // Equality is accepted: this fences REGRESSION, not re-statement, and a
      // controller re-sending the current context is not an error.
      //
      // ⚠️ CHECKED UNCONDITIONALLY, no longer only while a context is KNOWN.
      // `translationViewerContextInvalidated` fails the viewer closed while
      // RETAINING the watermark, precisely so a superseded snapshot cannot be
      // accepted afterwards. Gating this on `viewerContextKnown` would make
      // invalidation re-arm the regression it exists to refuse. Nothing is lost
      // at first context: the default is zero and the route contract forbids a
      // generation below zero (`route.v1.schema.json` `Generation.minimum: 0`),
      // so the first valid snapshot still satisfies `>= 0`.
      if (assignment_generation < state.viewerAssignmentGeneration) {
        refuse(
          state,
          'stale_assignment_generation',
          `viewer assignment ${assignment_generation} < ${state.viewerAssignmentGeneration}`,
        );
        return;
      }
      state.viewerRecipientId = recipient_id;
      state.viewerAssignmentGeneration = assignment_generation;
      state.viewerListenLanguage = listen_language;
      if (listen_language !== null) {
        state.captionListenLanguageMemory = listen_language;
      }
      state.viewerContextKnown = true;
      // BACKFILL. A route can be admitted before the controller has said who
      // this viewer is, so `addressesViewer` was false at admission and an
      // established language went unrecorded. The routes are already in state,
      // so this is a re-evaluation rather than new plumbing — and it fixes the
      // ordering WITHOUT weakening the addressing rule.
      for (const record of Object.values(state.observedEstablishments)) {
        promoteObservedEstablishment(state, record);
      }
    },

    /**
     * FAIL THIS VIEWER CLOSED, without pretending the call never happened.
     *
     * Dispatched when the controller-owned snapshot's lease expires, or when an
     * authoritative response says the viewer is no longer eligible. A stale
     * context is worse than no context: it keeps a language audible under facts
     * nobody has re-checked.
     *
     * WHAT IT CLEARS: the three CURRENT facts — who the viewer is, what they may
     * hear, and whether any of it is known.
     *
     * WHAT IT DELIBERATELY RETAINS, and why each would be a defect to clear:
     *  • The generation watermark. It is the fence that refuses a superseded
     *    snapshot; resetting it to zero would make the next stale response
     *    acceptable, so failing closed would REOPEN the hole.
     *  • `targetLanguageAssignmentFence`, which is meaningless except relative
     *    to `viewerAssignmentGeneration` and must reset in exactly the same
     *    places — a fence of 5 surviving into a generation that restarted at 0
     *    makes `0 > 5` false for ever.
     *  • `observedEstablishments` and `establishedLanguageGenerations`. These
     *    are HISTORY: what was in force and stopped. Wiping them would downgrade
     *    a language the participant was reading a second ago from "interrupted"
     *    to "never started" — the silent degradation this feature exists to
     *    prevent. The known-context gate belongs on PROMOTION, which only ever
     *    adds; nothing retracts.
     */
    translationViewerContextInvalidated: (state, action: PayloadAction<{ call_id: string }>) => {
      // Scoped like every other record. A late invalidation from a previous call
      // must not close the viewer on THIS one.
      if (state.callId === null || state.callId !== action.payload.call_id) {
        refuse(
          state,
          'call_scope_mismatch',
          `viewer context invalidation for ${action.payload.call_id}`,
        );
        return;
      }
      state.viewerContextKnown = false;
      state.viewerRecipientId = null;
      state.viewerListenLanguage = null;
      // Health goes with them. Invalidation exists to fail this viewer CLOSED,
      // and a surviving `healthy` is the one input that would keep asserting a
      // fact nobody has re-checked — the exact "stale healthy is worse than
      // unavailable" case this whole design is built around. Unlike the
      // generation watermark, health is not a fence against a superseded
      // snapshot, so there is nothing lost by clearing it.
      state.viewerWorkerHealth = WORKER_HEALTH_FAIL_CLOSED;
      // The roster rode the same lease: nobody has re-checked its relations
      // either, and a stale identity→language mapping mislabels who is being
      // shown what. Relations go; the generation FENCE stays, for exactly the
      // reason the assignment watermark above stays — a cleared fence would
      // re-admit the superseded roster this invalidation just retired.
      state.participantRoster.present = false;
      state.participantRoster.participants = [];
      state.participantRoster.unverified_count = 0;
    },

    /**
     * The controller's verdict on the translation worker, for THIS call.
     *
     * Its own action, not a field on `translationViewerContextSet`. That action
     * has 88 construction sites across eight files; a required addition would
     * touch every one of them, and an OPTIONAL one is a field a producer can
     * forget — which here means falling through to a default that decides what
     * somebody hears. A separate required action makes the omission a type
     * error at the one site that produces it.
     *
     * ⚠️ IT WRITES EXACTLY ONE FIELD. Health arrives on a different schedule
     * from the context — it expires locally, on a duration the context knows
     * nothing about — so a reducer that also refreshed the language would let an
     * expiry timer re-assert a context whose own horizon had passed.
     *
     * ⚠️ IT NEVER ESTABLISHES THE CALL SCOPE. `admitToCallScope` deliberately
     * adopts an unscoped call from the first record that names one, because a
     * route or a turn carries lineage. A health verdict carries none: adopting a
     * call from it would let a verdict arriving before `translationCallScopeSet`
     * define which call the whole slice is about.
     */
    translationViewerWorkerHealthSet: (
      state,
      action: PayloadAction<{ call_id: string; worker_health: WorkerHealth }>,
    ) => {
      const { call_id, worker_health } = action.payload;
      // VOCABULARY FIRST, and it is a membership test against the closed list —
      // never a truthiness check. `'Healthy'`, `''` and a value from a future
      // producer are all refused rather than coerced, because every one of them
      // would otherwise have to be given a meaning by this reducer, and the only
      // meaning available is the one that decides between translated and
      // original audio.
      if (!isWorkerHealth(worker_health)) {
        refuse(state, 'contract_invalid', `viewer worker health ${JSON.stringify(worker_health)}`);
        return;
      }
      if (state.callId === null || state.callId !== call_id) {
        refuse(state, 'call_scope_mismatch', `viewer worker health for ${call_id}`);
        return;
      }
      state.viewerWorkerHealth = worker_health;
    },

    /**
     * The server-verified participant roster, as read by the viewer-context
     * poll (own-language program C1).
     *
     * `present: false` is a deliberate CLEAR — the response carried no
     * verifiable roster, and retaining the previous relations would keep
     * asserting identity→language bindings nobody re-checked (design review
     * r1, finding 2). A clear ignores the generation guard and retains the
     * fence, mirroring `translationViewerContextInvalidated`.
     *
     * ⚠️ IT NEVER ESTABLISHES THE CALL SCOPE, for the health reducer's exact
     * reason: a roster carries no lineage a route or turn would, so adopting a
     * call from it would let a poll response define which call the slice is
     * about.
     */
    translationParticipantRosterSet: (
      state,
      action: PayloadAction<{
        call_id: string;
        present: boolean;
        participants: TranslationRosterParticipant[];
        roster_generation: number;
        /**
         * How many room participants the endpoint could NOT verify — REQUIRED
         * (r14 finding 2): the hook is the only producer and always has it, so
         * an optional field here would just be a place for a partial roster to
         * silently read as complete.
         */
        roster_unverified_count: number;
      }>,
    ) => {
      const { call_id, present, participants, roster_generation, roster_unverified_count } =
        action.payload;
      if (state.callId === null || state.callId !== call_id) {
        refuse(state, 'call_scope_mismatch', `participant roster for ${call_id}`);
        return;
      }
      if (!present) {
        state.participantRoster.present = false;
        state.participantRoster.participants = [];
        state.participantRoster.unverified_count = 0;
        return;
      }
      if (!isSafeGeneration(roster_generation)) {
        refuse(state, 'contract_invalid', `roster generation ${JSON.stringify(roster_generation)}`);
        return;
      }
      // Same shape rule as the generation: a count that is negative, fractional
      // or not a number is corruption, and admitting the rest of the roster
      // under it would present relations whose completeness cannot be judged.
      if (!isSafeGeneration(roster_unverified_count)) {
        refuse(
          state,
          'contract_invalid',
          `roster unverified_count ${JSON.stringify(roster_unverified_count)}`,
        );
        return;
      }
      // THE WHOLE UPDATE, or none of it: a roster with one malformed row is a
      // roster whose relations cannot be judged, and admitting the rest would
      // hand consumers a mapping that silently lost a participant.
      for (const row of participants) {
        const rowValid =
          typeof row.livekit_identity === 'string' &&
          row.livekit_identity.length > 0 &&
          typeof row.recipient_id === 'string' &&
          row.recipient_id.length > 0 &&
          (row.spoken_language === null || isLaunchLanguageCode(row.spoken_language)) &&
          (row.listen_language === null || isLaunchLanguageCode(row.listen_language)) &&
          (row.assignment_source === undefined ||
            row.assignment_source === null ||
            typeof row.assignment_source === 'string');
        if (!rowValid) {
          refuse(state, 'contract_invalid', `roster row ${JSON.stringify(row)}`);
          return;
        }
      }
      // MONOTONIC against the retained fence. Equality is accepted — this
      // fences REGRESSION, not re-statement, and membership can change (a
      // participant joins) without any assignment event moving the generation.
      if (roster_generation < state.participantRoster.generation) {
        refuse(
          state,
          'stale_assignment_generation',
          `roster ${roster_generation} < ${state.participantRoster.generation}`,
        );
        return;
      }
      state.participantRoster = {
        present: true,
        participants: participants.map((row) => ({ ...row })),
        generation: roster_generation,
        unverified_count: roster_unverified_count,
      };
      // ACCUMULATE the interim join map. First verified binding per identity
      // wins; a conflicting rebind is refused (see the field's doc); languages
      // for an unchanged binding track the newest roster.
      for (const row of participants) {
        const retained = state.rosterRetainedByIdentity[row.livekit_identity];
        if (retained && retained.recipient_id !== row.recipient_id) continue;
        // BOUNDED, fail-closed (review r2, Q5): identity churn can exceed the
        // concurrent-participant cap over a long call. Beyond the cap NEW
        // identities are refused rather than old verified bindings evicted —
        // an unretained speaker classifies as unknown (pending, never
        // mislabeled), while evicting could strip protection from text
        // already attributed.
        if (retained === undefined && Object.keys(state.rosterRetainedByIdentity).length >= 128) {
          continue;
        }
        state.rosterRetainedByIdentity[row.livekit_identity] = { ...row };
      }
    },

    /**
     * Admit a COMPLETE route envelope, validated against the frozen contract and
     * fenced per publication: a lower `route_generation` for the same
     * `publication_sid` is stale and is refused, not applied.
     *
     * Every envelope is stored; which one governs a given turn is decided at
     * read time by {@link selectGoverningRoute}, so an unrelated route in the
     * same language can never make a turn look delayed or unavailable.
     */
    translationRouteObserved: (state, action: PayloadAction<RouteEnvelopeV1>) => {
      const envelope = action.payload;
      const result = validateRouteEnvelopeV1(envelope);
      if (!result.valid) {
        refuse(state, 'contract_invalid', `route: ${describeErrors(result.errors)}`);
        return;
      }
      if (!admitToCallScope(state, envelope.call_id, 'route')) return;

      // BOTH generations are fenced per publication, exactly as the approved
      // `RouteRegistry.staleReason` does. Fencing only `route_generation` would
      // let a republished envelope carrying an assignment generation the viewer
      // has already moved past overwrite a current one (BI-11).
      const existing = state.routes[envelope.publication_sid];
      if (existing) {
        const stale =
          envelope.route_generation < existing.route_generation
            ? ([
                'stale_route_generation',
                envelope.route_generation,
                existing.route_generation,
              ] as const)
            : envelope.assignment_generation < existing.assignment_generation
              ? ([
                  'stale_assignment_generation',
                  envelope.assignment_generation,
                  existing.assignment_generation,
                ] as const)
              : null;
        if (stale) {
          refuse(
            state,
            stale[0],
            `publication ${envelope.publication_sid} ${stale[1]} < ${stale[2]}`,
          );
          return;
        }
      }
      state.routes[envelope.publication_sid] = envelope;
      observeEstablishment(state, envelope);
      sweepConfirmingPairs(state);
      if (envelope.state === 'ended') {
        // Close the interval on every retained observation of this
        // publication (sol r60 finding 1): a departed listener's delivery
        // must not pair with a producer that establishes only later.
        for (const record of Object.values(state.observedEstablishments)) {
          if (
            record.publication_sid === envelope.publication_sid &&
            record.ended_at_order === null
          ) {
            state.observationOrder += 1;
            record.ended_at_order = state.observationOrder;
          }
        }
      }
    },

    /**
     * Admit a source turn's LINEAGE. The record is validated in full against the
     * frozen contract and then its `text` is DROPPED — this slice never holds
     * source text (see the header).
     */
    translationSourceTurnObserved: (state, action: PayloadAction<SourceTurnV1>) => {
      const record = action.payload;
      const result = validateSourceTurnV1(record);
      if (!result.valid) {
        refuse(state, 'contract_invalid', `source turn: ${describeErrors(result.errors)}`);
        return;
      }
      if (!admitToCallScope(state, record.call_id, 'source turn')) return;
      // The ledger is immutable; a replay is a no-op, never an update — except
      // the ONE in-place mutation the database itself performs (A4): a covering
      // provider final superseding a rescue, which only moves the generation
      // forward. Lower or equal generations are the replays they are.
      const existing = state.turns[record.source_turn_id];
      if (existing) {
        const incoming = record.supersede_generation ?? 0;
        if (incoming > (existing.supersede_generation ?? 0)) existing.supersede_generation = incoming;
        return;
      }

      state.turns[record.source_turn_id] = {
        source_turn_id: record.source_turn_id,
        call_id: record.call_id,
        deepgram_segment_id: record.deepgram_segment_id,
        stable_participant_id: record.stable_participant_id,
        source_epoch: record.source_epoch,
        source_track_sid: record.source_track_sid,
        ordinal: record.ordinal,
        started_at_ms: record.started_at_ms,
        source_language: record.source_language,
        detected_language: record.detected_language ?? null,
        supersede_generation: record.supersede_generation ?? 0,
      };

      const claimants = state.turnIdsBySegmentId[record.deepgram_segment_id] ?? [];
      claimants.push(record.source_turn_id);
      state.turnIdsBySegmentId[record.deepgram_segment_id] = claimants;
      if (claimants.length > 1) {
        // Recorded, not resolved. A segment id claimed by two turns cannot pick
        // one of them without guessing, so the join refuses it at read time.
        refuse(
          state,
          'ambiguous_segment_lineage',
          `segment ${record.deepgram_segment_id} is claimed by ${claimants.length} source turns`,
        );
      }
    },

    /**
     * State the authoritative correlation between a transcript message and the
     * source turn it came from.
     *
     * This is the ONLY way a bubble is ever joined to a turn. The key is the
     * message's own `syncId` (`lib/transcripts/persistence.ts`), because that is
     * the identity the transcript pipeline already stamps on the event; the
     * slice never composes or guesses one.
     *
     * Two different turns correlated to one sync id is a contradiction in the
     * producer, not something to resolve here. The key is marked ambiguous
     * PERMANENTLY and both sides stop joining, so the display falls back to
     * awaiting-lineage instead of settling on a confident wrong answer.
     */
    translationTranscriptCorrelated: (
      state,
      action: PayloadAction<{ transcript_sync_id: string; source_turn_id: string }>,
    ) => {
      const { transcript_sync_id, source_turn_id } = action.payload;
      if (!transcript_sync_id) {
        refuse(state, 'contract_invalid', 'correlation has an empty transcript sync id');
        return;
      }
      // A locally-derived identity is not unique across a republication, so a
      // correlation stated against one would be reused by a later utterance.
      if (!isJoinableTranscriptIdentity(transcript_sync_id)) {
        refuse(
          state,
          'non_authoritative_transcript_identity',
          `sync id ${transcript_sync_id} is derived locally and is not unique across source epochs`,
        );
        return;
      }
      if (!state.turns[source_turn_id]) {
        refuse(state, 'unknown_source_turn', `correlation cites unadmitted turn ${source_turn_id}`);
        return;
      }
      if (state.ambiguousTranscriptSyncIds[transcript_sync_id]) {
        refuse(
          state,
          'ambiguous_transcript_correlation',
          `sync id ${transcript_sync_id} is already known to be ambiguous`,
        );
        return;
      }
      const existing = state.sourceTurnIdByTranscriptSyncId[transcript_sync_id];
      if (existing !== undefined && existing !== source_turn_id) {
        state.ambiguousTranscriptSyncIds[transcript_sync_id] = true;
        delete state.sourceTurnIdByTranscriptSyncId[transcript_sync_id];
        refuse(
          state,
          'ambiguous_transcript_correlation',
          `sync id ${transcript_sync_id} correlated to both ${existing} and ${source_turn_id}`,
        );
        return;
      }
      state.sourceTurnIdByTranscriptSyncId[transcript_sync_id] = source_turn_id;
    },

    /**
     * Append a revision. History is append-only: a revision is never edited and
     * never removed, and appending one does NOT change what is displayed —
     * selection does that, as a separate event.
     */
    translationRevisionAppended: (
      state,
      action: PayloadAction<TranslationRevisionRecordV1>,
    ) => {
      const record = action.payload;
      // The frozen 19-key core OR the 25-key attached shape (20261164); the
      // six attachment keys are kept on the stored record exactly as sent —
      // they are the worker's attachment truth the no-retraction display
      // invariant consumes. A PARTIAL set is refused by name, never coerced.
      const result = validateTranslationRevisionRecord(record);
      if (!result.valid) {
        refuse(
          state,
          'contract_invalid',
          result.reason === 'revision_invalid'
            ? `revision: ${describeErrors(result.errors)}`
            : `revision ${result.reason}: ${result.detail}`,
        );
        return;
      }
      if (!admitToCallScope(state, record.call_id, 'revision')) return;

      const turn = state.turns[record.source_turn_id];
      if (!turn) {
        refuse(
          state,
          'unknown_source_turn',
          `revision ${record.translation_revision_id} cites unadmitted turn ${record.source_turn_id}`,
        );
        return;
      }
      if (turn.call_id !== record.call_id) {
        refuse(
          state,
          'call_scope_mismatch',
          `revision ${record.translation_revision_id} call ${record.call_id} != turn call ${turn.call_id}`,
        );
        return;
      }

      const key = pairKey(record.source_turn_id, record.target_language);
      const history = state.revisions[key] ?? [];
      // At-least-once delivery: replaying a revision is a no-op.
      if (
        history.some((entry) => entry.translation_revision_id === record.translation_revision_id)
      ) {
        return;
      }
      // BI-05: (source_turn_id, target_language, revision_index) is unique.
      if (history.some((entry) => entry.revision_index === record.revision_index)) {
        refuse(
          state,
          'revision_lineage_conflict',
          `two revisions claim index ${record.revision_index} for ${record.source_turn_id}/${record.target_language}`,
        );
        return;
      }
      // D4c (rule 121): A SETTLED ROW NEVER MOVES. The producer and the SQL
      // (`revision_settled`) refuse this already; the store refuses it
      // independently, counted, with the words in the detail (rule 120: they are
      // recorded, never silently dropped).
      const latest = history.reduce<TranslationRevisionRecordV1 | null>(
        (top, entry) => (top === null || entry.revision_index > top.revision_index ? entry : top),
        null,
      );
      if (
        latest !== null &&
        settlementOfRecord(latest) === 'final_attached' &&
        record.revision_index > latest.revision_index
      ) {
        refuse(
          state,
          'revision_after_settlement',
          `revision ${record.translation_revision_id} (index ${record.revision_index}) stacked on settled ` +
            `row ${latest.translation_revision_id} (index ${latest.revision_index}) for ` +
            `${record.source_turn_id}/${record.target_language}; words: ${JSON.stringify(record.text)}`,
        );
        return;
      }
      history.push(record);
      history.sort((a, b) => a.revision_index - b.revision_index);
      state.revisions[key] = history;
    },

    /**
     * Choose which appended revision is displayed. A pair has exactly one current
     * revision; the rest stay in history. Selecting a revision that was never
     * appended is refused — the display never invents text.
     *
     * D2: it carries a `selection_generation` and no-ops any selection whose
     * generation does not advance the one already applied (from the live
     * selection or, across a remount, the watermark), so a reordered or replayed
     * live packet cannot move the display backwards.
     */
    translationRevisionSelected: (
      state,
      action: PayloadAction<{
        source_turn_id: string;
        target_language: LaunchLanguageCode;
        translation_revision_id: string;
        /**
         * The selection's generation (D2). Optional ONLY so the body of pre-fence
         * unit tests that dispatch a bare selection stay valid; every production
         * dispatch carries one (the wire envelope requires it, and the hydration
         * replay reports it), so the fence below is fully active in production.
         */
        selection_generation?: number;
      }>,
    ) => {
      const { source_turn_id, target_language, translation_revision_id, selection_generation } =
        action.payload;
      if (!isLaunchLanguageCode(target_language)) {
        refuse(
          state,
          'language_not_in_catalog',
          `selection language ${JSON.stringify(target_language)}`,
        );
        return;
      }
      // A malformed generation is a producer defect, not a stale one: refuse it
      // rather than silently treating it as "no generation" (which would disarm
      // the fence). 0 is legitimate — the first selection is generation 0.
      if (
        selection_generation !== undefined &&
        (!Number.isSafeInteger(selection_generation) || selection_generation < 0)
      ) {
        refuse(
          state,
          'contract_invalid',
          `selection generation ${JSON.stringify(selection_generation)}`,
        );
        return;
      }

      const key = pairKey(source_turn_id, target_language);
      const existing = state.selections[key];
      const watermark = state.selectionWatermarks[key];

      // ── D2 GENERATION FENCE ────────────────────────────────────────────────
      // A selection whose generation does NOT advance the last one applied is a
      // reordered or replayed live packet, and applying it would move the display
      // backwards. No-op it — silently, because a stale duplicate is expected
      // at-least-once traffic, not a contract violation to count. The prior
      // generation comes from the live selection OR, after a transient teardown
      // when there is none, from the watermark, so the fence survives a remount.
      const priorGen = existing?.selection_generation ?? watermark?.selection_generation;
      if (
        selection_generation !== undefined &&
        priorGen !== undefined &&
        selection_generation <= priorGen
      ) {
        return;
      }

      const chosen = state.revisions[key]?.find(
        (entry) => entry.translation_revision_id === translation_revision_id,
      );
      if (!chosen) {
        refuse(
          state,
          'unknown_revision',
          `selection names unappended revision ${translation_revision_id}`,
        );
        return;
      }
      // The comparison is against the last revision the READER WAS SHOWN, taken
      // from the live selection or — after a transient teardown, when there is
      // no live selection — from the watermark. Comparing only against a live
      // selection made every change that spanned a remount invisible.
      const priorRevisionId =
        existing?.selected_revision_id ?? watermark?.last_selected_revision_id;
      const priorCount = existing?.selection_change_count ?? watermark?.selection_change_count ?? 0;
      const firstSelectedRevisionId =
        existing?.first_selected_revision_id ??
        watermark?.first_selected_revision_id ??
        translation_revision_id;
      // Rule 111 (catalog B6): a correction carrying the SAME text as what it
      // replaces is no visible event. A new revision id alone lit "Revised";
      // the reader saw nothing change. Compare the text the reader was shown
      // (when that revision is still in the store) with the text now chosen.
      const priorText =
        priorRevisionId !== undefined
          ? state.revisions[key]?.find((entry) => entry.translation_revision_id === priorRevisionId)?.text
          : undefined;
      const changed =
        priorRevisionId !== undefined &&
        priorRevisionId !== translation_revision_id &&
        (priorText === undefined || priorText !== chosen.text);
      const nextCount = priorCount + (changed ? 1 : 0);
      // PRESERVE the high-water generation when this dispatch carries none, so a
      // legacy gen-less selection cannot wipe the fence's memory and let a later
      // stale packet through.
      const nextGen = selection_generation ?? priorGen;
      state.selections[key] = {
        selected_revision_id: translation_revision_id,
        first_selected_revision_id: firstSelectedRevisionId,
        selection_change_count: nextCount,
        selection_generation: nextGen,
      };
      state.selectionWatermarks[key] = {
        selection_change_count: nextCount,
        last_selected_revision_id: translation_revision_id,
        first_selected_revision_id: firstSelectedRevisionId,
        selection_generation: nextGen,
      };
      // A selection resolves the pair, so an earlier structural marker for it no
      // longer describes reality.
      delete state.unavailable[key];
    },

    /**
     * ENGINE ISLAND (D1b / TL-01, rule 120 ABSOLUTE): engine words that attached
     * to no source turn. Validated by structure, admitted under the call scope,
     * keyed by the worker-derived id so a redelivery is the same island.
     */
    translationEngineIslandObserved: (state, action: PayloadAction<EngineIslandV1>) => {
      const record = action.payload;
      const result = validateEngineIslandV1(record);
      if (!result.valid) {
        refuse(state, 'contract_invalid', `island ${describeErrors(result.errors)}`);
        return;
      }
      if (!admitToCallScope(state, record.call_id, `island ${record.engine_island_id}`)) return;
      state.islands[record.engine_island_id] = record;
    },

    /**
     * Structural marker: this pair will not receive a translation. Never inferred
     * from a timeout or from text — only recorded when something upstream says so.
     */
    translationTurnMarkedUnavailable: (
      state,
      action: PayloadAction<{
        source_turn_id: string;
        target_language: LaunchLanguageCode;
        reason_code: string;
      }>,
    ) => {
      const { source_turn_id, target_language, reason_code } = action.payload;
      if (!isLaunchLanguageCode(target_language)) {
        refuse(
          state,
          'language_not_in_catalog',
          `marker language ${JSON.stringify(target_language)}`,
        );
        return;
      }
      if (!state.turns[source_turn_id]) {
        refuse(state, 'unknown_source_turn', `marker cites unadmitted turn ${source_turn_id}`);
        return;
      }
      state.unavailable[pairKey(source_turn_id, target_language)] = { reason_code };
    },

    /**
     * Admit the projection RPC's attested merged record for a pair (P3's
     * additive `merged` rows; owner design 2026-09-02). Stored as evidence,
     * never displayed blindly: {@link mergedRecordFor} uses it only while its
     * `translation_text` is the revision actually selected, so a stale
     * attestation can never put words on screen that the ledger has moved past.
     * The server's `merged_text` must be EXACTLY `composeMergedRecord` of its
     * own halves — a projection that composes differently is a contract
     * defect refused here, never rendered. Neither text half is retained (the
     * slice holds no source text). At-least-once: a replay overwrites with an
     * identical record.
     */
    translationMergedRecordObserved: (
      state,
      action: PayloadAction<{
        call_id: string;
        source_turn_id: string;
        target_language: LaunchLanguageCode;
        source_text: string;
        translation_text: string | null;
        merged_text: string;
        origin: TranslationOrigin | null;
        request_contract_version: string | null;
      }>,
    ) => {
      const record = action.payload;
      if (
        typeof record.call_id !== 'string' ||
        typeof record.source_turn_id !== 'string' ||
        typeof record.source_text !== 'string' ||
        typeof record.merged_text !== 'string' ||
        (record.translation_text !== null && typeof record.translation_text !== 'string') ||
        (record.origin !== null && !isTranslationOrigin(record.origin)) ||
        (record.request_contract_version !== null &&
          typeof record.request_contract_version !== 'string')
      ) {
        refuse(state, 'contract_invalid', 'merged record: field types');
        return;
      }
      if (!isLaunchLanguageCode(record.target_language)) {
        refuse(
          state,
          'language_not_in_catalog',
          `merged record language ${JSON.stringify(record.target_language)}`,
        );
        return;
      }
      if (!admitToCallScope(state, record.call_id, 'merged record')) return;
      const turn = state.turns[record.source_turn_id];
      if (!turn) {
        refuse(
          state,
          'unknown_source_turn',
          `merged record cites unadmitted turn ${record.source_turn_id}`,
        );
        return;
      }
      if (turn.call_id !== record.call_id) {
        refuse(
          state,
          'call_scope_mismatch',
          `merged record call ${record.call_id} != turn call ${turn.call_id}`,
        );
        return;
      }
      if (record.merged_text !== composeMergedRecord(record.source_text, record.translation_text)) {
        refuse(state, 'contract_invalid', 'merged record: merged_text is not "<source> [<translation>]"');
        return;
      }
      state.mergedRecords[pairKey(record.source_turn_id, record.target_language)] = {
        translation_text: record.translation_text,
        origin: record.origin,
        request_contract_version: record.request_contract_version,
      };
    },

    /**
     * Record which revision's audio the listener actually heard.
     *
     * THE ONLY writer of `heardRevisionId`. Appending or selecting a revision
     * must never write it, or a later text revision would retroactively claim to
     * be what was played.
     */
    translationAudioDeliveryObserved: (
      state,
      action: PayloadAction<{
        source_turn_id: string;
        target_language: LaunchLanguageCode;
        translation_revision_id: string;
      }>,
    ) => {
      const { source_turn_id, target_language, translation_revision_id } = action.payload;
      if (!isLaunchLanguageCode(target_language)) {
        refuse(
          state,
          'language_not_in_catalog',
          `delivery language ${JSON.stringify(target_language)}`,
        );
        return;
      }
      const key = pairKey(source_turn_id, target_language);
      const known = state.revisions[key]?.some(
        (entry) => entry.translation_revision_id === translation_revision_id,
      );
      if (!known) {
        refuse(
          state,
          'unknown_revision',
          `delivery names unappended revision ${translation_revision_id}`,
        );
        return;
      }
      state.heardRevisionId[key] = translation_revision_id;
    },

    /**
     * Pin a quote by IDENTITY. No text is copied: the quote renders whatever
     * revision is current and reports that the selection moved. Fails closed —
     * an empty or partly unadmitted turn list is refused rather than pinned to a
     * subset the reader would have no way to notice.
     */
    translationQuotePinned: (
      state,
      action: PayloadAction<{
        quote_id: string;
        scope: TranslationQuoteScope;
        target_language: LaunchLanguageCode;
        source_turn_ids: string[];
        /** The correlations that resolved those turns, for later revalidation. */
        lineage: TranscriptTurnCorrelation[];
        /**
         * Whether the bubble's lineage was COMPLETE at pin time. Defaults to
         * true only when omitted by a caller that has no bubble.
         */
        lineage_complete?: boolean;
      }>,
    ) => {
      const { quote_id, scope, target_language, source_turn_ids, lineage } = action.payload;
      // Defence in depth behind the tile's guard: a quote must never be created
      // over a bubble that was only partly identified, or the pinboard preserves
      // a translation covering part of what was said as though it covered all.
      if (action.payload.lineage_complete === false) {
        refuse(state, 'unknown_source_turn', `quote ${quote_id} pinned over incomplete lineage`);
        return;
      }
      if (!isLaunchLanguageCode(target_language)) {
        refuse(
          state,
          'language_not_in_catalog',
          `quote language ${JSON.stringify(target_language)}`,
        );
        return;
      }
      if (source_turn_ids.length === 0) {
        refuse(state, 'unknown_source_turn', `quote ${quote_id} names no source turn`);
        return;
      }
      const unknown = source_turn_ids.filter((id) => !state.turns[id]);
      if (unknown.length > 0) {
        refuse(
          state,
          'unknown_source_turn',
          `quote ${quote_id} cites unadmitted turns ${unknown.join(', ')}`,
        );
        return;
      }
      const pinned: Record<string, string | null> = {};
      const pinnedCounts: Record<string, number> = {};
      for (const sourceTurnId of source_turn_ids) {
        const selection = state.selections[pairKey(sourceTurnId, target_language)];
        pinned[sourceTurnId] = selection ? selection.selected_revision_id : null;
        pinnedCounts[sourceTurnId] = selection ? selection.selection_change_count : 0;
      }
      state.quotes[quote_id] = {
        quote_id,
        scope,
        target_language,
        source_turn_ids: [...source_turn_ids],
        pinned_revision_ids: pinned,
        pinned_selection_change_counts: pinnedCounts,
        pinned_lineage: lineage.map((entry) => ({ ...entry })),
      };
    },

    translationQuoteReleased: (state, action: PayloadAction<string>) => {
      if (!state.quotes[action.payload]) {
        refuse(state, 'unknown_quote', `release names unknown quote ${action.payload}`);
        return;
      }
      delete state.quotes[action.payload];
    },

    /**
     * Teardown. The viewer's language choice and collapse preference survive.
     *
     * DEFAULTS TO FULL DESTRUCTION, deliberately. Every existing caller means
     * "this call is over", and a sticky default would leak a previous call's
     * routes and quotes into the next one — fixing one defect by creating a
     * worse one. `{ scope: 'session' }` is the narrow, opt-in transient form for
     * an unmount within the same call, which retains the monotonic watermarks
     * and the pinned quotes and keeps the call scope.
     */
    clearTranslations: (
      state,
      action: PayloadAction<{ scope: 'session' | 'call' } | undefined>,
    ) => {
      if (action.payload?.scope === 'session') {
        resetSessionScopedState(state);
        state.rejected = { count: 0, last_reason_code: null, last_detail: null };
        return;
      }
      state.callId = null;
      resetCallScopedState(state);
    },
  },
});

/**
 * The reducer, CAPTURED at creation and exported as an immutable binding.
 *
 * `createSlice` returns an object whose `reducer` property is writable, so
 * `translationSlice.reducer = patched` — or `Object.assign(translationSlice,
 * { reducer: patched })` — could otherwise substitute a wrapper that intercepts
 * every action while every audited reducer, action creator and export remained
 * exactly as written.
 *
 * These two statements are IMMEDIATELY ADJACENT to the `createSlice` call, and
 * the audit asserts their exact statement positions. Adjacency is the whole
 * argument: no statement can run between creation and capture, so the binding
 * below is the function that existed at creation, and after the freeze any later
 * substitution throws under ES module strict mode. A gap here — even one
 * innocuous line — reopens the bypass.
 *
 * Do not move, reorder, or inline these.
 */
const auditedTranslationReducer = translationSlice.reducer;
Object.freeze(translationSlice);

export const {
  translationEngineIslandObserved,
  translationTargetLanguageSelected,
  translationTargetLanguageAutoResolved,
  translationTranscriptViewModeSet,
  translationRevealAnnounced,
  callerTranslationNoticeRecorded,
  translationListenAudioChoiceSelected,
  translationDeliveryIntentAcknowledged,
  translationDeliveryIntentFenceObserved,
  translationCallScopeSet,
  translationViewerContextSet,
  translationViewerContextInvalidated,
  translationViewerWorkerHealthSet,
  translationParticipantRosterSet,
  translationRouteObserved,
  translationSourceTurnObserved,
  translationTranscriptCorrelated,
  translationRevisionAppended,
  translationRevisionSelected,
  translationTurnMarkedUnavailable,
  translationMergedRecordObserved,
  translationAudioDeliveryObserved,
  translationQuotePinned,
  translationQuoteReleased,
  clearTranslations,
} = translationSlice.actions;

export default auditedTranslationReducer;

// ---------------------------------------------------------------------------
// views
// ---------------------------------------------------------------------------

export interface TranslationTurnView {
  source_turn_id: string;
  /**
   * The earlier turn whose translation already carries this turn's words (the engine ran
   * across the turn boundary). Read by TranscriptionTile's carried branch (b4825ce4b). No producer
   * sets it yet, so today it is always absent and the tile behaves as before.
   */
  carried_by_turn_id?: string | null;
  target_language: LaunchLanguageCode;
  /**
   * The retained selected translation. NEVER cleared by a route condition and
   * never the source text. `null` only when no revision has been selected.
   */
  text: string | null;
  content: TranslationContentStatus;
  route_condition: TranslationRouteCondition;
  /** Independent of the operational badge; see {@link translationTurnBadges}. */
  revised: boolean;
  translation_revision_id: string | null;
  revision_index: number | null;
  /** Full append-only history size. A revision is never discarded. */
  revision_count: number;
  delivery_state: DeliveryState | null;
  unavailable_reason: string | null;
  nuance_annotations: NuanceAnnotation[];
  /** The revision whose audio was heard. A text revision never changes it. */
  heard_revision_id: string | null;
  /**
   * THE MERGED RECORD (owner design 2026-09-02): which arm produced the
   * selected revision's words, from the row's own `request_contract_version`
   * (`translationOriginFromContract`) — `'engine'` = the words the officer
   * heard, `'fallback'` = a plain terra re-translation, `null` = the row does
   * not say. An attested record's origin fills in only when the row is silent.
   */
  origin: TranslationOrigin | null;
  request_contract_version: string | null;
  /**
   * "<source text> [<selected translation>]", or the source alone while
   * unsettled — composed from the TRANSCRIPT's source text (a join input; the
   * slice holds none). `null` when no transcript text was in view.
   */
  merged_text: string | null;
  /** True when the projection RPC attested this pair for the revision on screen. */
  merged_text_attested: boolean;
  /**
   * THE WORKER'S ATTACHMENT TRUTH for the selected revision (20261164; sol
   * verdict on board run 23): confidence, basis, allocation group and the
   * half-open [start, end) engine-stream range in CODE POINTS. `null` when no
   * revision is selected or the selected record is the frozen 19-key core
   * (attachment UNKNOWN — never guessed). Optional in the type only so
   * hand-built views in older tests still compile; the builder always sets it.
   */
  attachment?: RevisionAttachmentV1 | null;
  /**
   * D4c (TL-02, rule 121): the selected row's settlement as the WIRE carried it —
   * `final_attached` rows are final on every surface; `null` when the record
   * carries none (an older producer), in which case the attachment inference
   * decides `provisional`. Optional only so hand-built test views compile.
   */
  settlement?: TurnSettlement | null;
}

/** D4c: a translated row's settlement (D4a readers, D4b producer). */
export type TurnSettlement = 'provisional' | 'final_attached';

/** The record's settlement when it carries a valid one, else null (never defaulted). */
function settlementOfRecord(record: object): TurnSettlement | null {
  const value = (record as Record<string, unknown>).settlement;
  return value === 'provisional' || value === 'final_attached' ? value : null;
}

export interface BubbleTranslationView {
  bubble_id: string;
  /** How many of this bubble's owed turns are carried by an earlier turn (see TranslationTurnView.carried_by_turn_id). No producer yet. */
  carried_turn_count?: number;
  target_language: LaunchLanguageCode;
  /**
   * The language the bubble's resolved turns were SPOKEN in, so the tag can read
   * "Chinese → English" (owner 2026-09-11). One distinct language, else null —
   * mixed or unknown is never guessed. Optional in the type only so hand-built
   * views in older tests still compile; the builder always sets it.
   */
  source_language?: LaunchLanguageCode | null;
  /** Retained texts of ALL turns, joined in display order. */
  text: string | null;
  content: TranslationContentStatus;
  badges: TranslationBadge[];
  turns: TranslationTurnView[];
  turn_count: number;
  translated_turn_count: number;
  /** Survives the badge reduction so a revision is never lost in a mixed bubble. */
  revised_turn_count: number;
  awaiting_turn_count: number;
  unavailable_turn_count: number;
  /**
   * False when part of this bubble has no usable lineage, so the text above
   * covers only part of what was said. The renderer must say so; a partial
   * translation presented as a whole one is an evidence defect.
   */
  lineage_complete: boolean;
  unresolved_message_count: number;
  /**
   * The bubble's provenance, reduced from its turns: `'fallback'` when ANY
   * translated turn came from the plain re-translation arm (the reader must
   * see it), `'engine'` when every translated turn carries the engine's words,
   * `null` when nothing translated or no row says.
   */
  origin: TranslationOrigin | null;
  /**
   * NO RETRACTION (sol verdict on board run 23, 2026-09-02): true when ANY
   * translated turn's selected revision carries an attachment that is not
   * `exact` — a bounded head-prefix, a joint attachment. The caption keeps
   * its live suffix behind such a row; it is a prefix of the turn, not the
   * turn. False for exact rows and for legacy rows whose attachment is
   * unknown (never guessed). Optional in the type only so hand-built views
   * in older tests still compile; the builder always sets it.
   */
  provisional?: boolean;
}

export interface TranslationQuoteView {
  quote_id: string;
  scope: TranslationQuoteScope;
  target_language: LaunchLanguageCode;
  turns: TranslationTurnView[];
  /**
   * `source_is_target` when every pinned turn was already spoken in the target
   * language, so no translation is owed and no line should be rendered. Without
   * it, such a quote would sit under "waiting for the translation" for ever.
   */
  content: TranslationContentStatus;
  /** Always the CURRENT revisions, never a copy taken at pin time. */
  text: string | null;
  changed_since_pin: boolean;
  changed_turn_count: number;
  badges: TranslationBadge[];
  /**
   * False when the lineage this quote was pinned on no longer holds — the
   * correlation was contradicted or superseded. The translation is withheld and
   * the surface says why; it is never shown as though still supported.
   */
  lineage_complete: boolean;
  /**
   * True for a span pin: there is no id-based mapping from a selected text range
   * to part of a translation, and deriving one would mean matching text. The
   * translation shown covers the WHOLE turn the selection came from, and the
   * renderer says so rather than implying the span was translated on its own.
   */
  covers_whole_turn: boolean;
}

interface TranslationRootState {
  translation: TranslationState;
  transcription: TranscriptionState;
}

const selectTranslationSlice = (state: TranslationRootState): TranslationState => state.translation;

export const selectTranslationTargetLanguage = (
  state: TranslationRootState,
): LaunchLanguageCode | null => state.translation.targetLanguage;

/** TRUE when the current target language was auto-resolved, never picked. */
/** Every admitted island, in session then engine-range order (the hydration read's order). */
export const selectTranslationEngineIslands = createSelector(
  (state: TranslationRootState) => state.translation.islands,
  (islands): EngineIslandV1[] =>
    Object.values(islands).sort(
      (a, b) =>
        a.translation_session_record_id.localeCompare(b.translation_session_record_id) ||
        a.engine_range_start - b.engine_range_start,
    ),
);

export const selectTranslationTargetLanguageAuto = (state: TranslationRootState): boolean =>
  state.translation.targetLanguageAuto;

const NO_CALLER_NOTICES: CallerTranslationNoticeRecord[] = Object.freeze(
  [],
) as unknown as CallerTranslationNoticeRecord[];

/** The current call's caller-surface notices, oldest first (stable empty). */
export const selectCallerTranslationNotices = (
  state: TranslationRootState,
): CallerTranslationNoticeRecord[] =>
  (state.translation.callId !== null &&
    state.translation.callerNoticesByCallId?.[state.translation.callId]) ||
  NO_CALLER_NOTICES;

export const selectTranslationRevealAnnounced = (state: TranslationRootState): boolean =>
  state.translation.callId !== null &&
  state.translation.announcedCallIds[state.translation.callId] === true;

export const selectTranslationTranscriptViewMode = (
  state: TranslationRootState,
): TranscriptViewMode => state.translation.transcriptViewMode;

/**
 * The RAW choice, for a surface that has to tell "not asked" from "chose the
 * original". Nothing routing-related may read this — see the mapping below.
 */
export const selectTranslationListenAudioChoice = (
  state: TranslationRootState,
): TranslationListenAudioChoice => state.translation.listenAudioChoice;

/**
 * THE ONLY SITE that turns the participant's choice into
 * `ListenerContext.preferOriginal`.
 *
 * ⚠️ OWNER RULE 2026-08-15 — "NEVER DEFAULT FEATURES TO OFF". The first cut of
 * this selector required the participant to have EXPLICITLY chosen
 * `'translated'` before any translated audio could play, and defaulted `null`
 * to the original voice. The owner's live trial heard the original language and
 * called it "audio translation doesn't work at all". So:
 *
 *   `null`          = AUTO. Follow the LEDGER: the reconciler writes a
 *                     `policy_default` delivery intent of `'translated'` for a
 *                     listener whose language differs from a speaker's, on the
 *                     listener's behalf, without anyone pressing anything. When
 *                     the browser has learned that row (`deliveryIntentAcknowledged
 *                     === 'translated'` — from its own write OR from the
 *                     viewer-context read), translated audio plays.
 *   `'translated'`  = the person asked; same gate on the acknowledgement.
 *   `'original'`    = the person opted OUT. Immediate, and it wins over
 *                     everything the ledger says. This is the only value that
 *                     ignores the acknowledgement, and it ignores it in the
 *                     refusing direction.
 *
 * ⚠️ WHAT IS STILL FAIL-SAFE. The acknowledgement operand is call-scoped: a new
 * call re-enters the waiting state and plays the ORIGINAL until the ledger for
 * THAT call says translated. Nothing here is optimistic in the translated
 * direction — the browser never plays a translation the durable ledger has not
 * named for this listener in this call. `RoutePolicyEngine` additionally
 * demotes every translated route while `workerHealth !== 'healthy'`; that
 * disjunct is untouched by this rule.
 *
 * ⚠️ WHAT MOVED. `useTranslationViewerContext` may now PROMOTE on a polled
 * `'translated'` (it used to demote only). The reason the demote-only design
 * gave — "a confirmation nobody gave for call B" — assumed the person was the
 * only legitimate author of a translated intent. Under the zero-touch model the
 * POLICY is an author, and the row it writes for call B is a confirmation FOR
 * call B.
 *
 *   original → translated  still waits for the acknowledgement (never optimistic).
 *   translated → original  is IMMEDIATE (`'original'` short-circuits).
 */
export const selectTranslationPreferOriginal = (state: TranslationRootState): boolean => {
  if (state.translation.listenAudioChoice === 'original') return true;
  return state.translation.deliveryIntentAcknowledged !== 'translated';
};

/**
 * The server-acknowledged intent, for the surface that has to tell a
 * participant their request is still in flight. Nothing routing-related may
 * read this on its own — audibility goes through the single mapping above.
 */
export const selectTranslationDeliveryIntentAcknowledged = (
  state: TranslationRootState,
): TranslationListenAudioChoice => state.translation.deliveryIntentAcknowledged;

/**
 * The acknowledged generation, which is exactly what the next request must send
 * as `expected_previous_generation`. `0` means "no acknowledged row", which is
 * the value the append RPC wants for a first write.
 */
export const selectTranslationDeliveryIntentGeneration = (state: TranslationRootState): number =>
  state.translation.deliveryIntentGeneration;

export const selectTranslationRejections = (state: TranslationRootState): TranslationRejectionLog =>
  state.translation.rejected;

/**
 * What the AUDIO router is allowed to know about this viewer.
 *
 * The fields matching `ListenerContext`'s `listenLanguage`,
 * `assignmentGeneration` and `workerHealth`, because the router's policy already
 * defines what they mean and a second definition would drift. (Deliberately no
 * count in this sentence: a count in a comment is a fact that goes stale the
 * moment somebody adds or removes a field, which has now happened twice.)
 *
 * ⚠️ The participant's hear-original choice is deliberately NOT here. These
 * travel together because they share a fate — the closed context has to be
 * closed in all of them at once — whereas `listenAudioChoice` is a PREFERENCE
 * that survives every reset, and putting it in this object would tie it to a
 * lifecycle it does not have. It reaches the router through
 * `selectTranslationPreferOriginal`, its own single mapping site.
 */
export interface TranslationListenerContext {
  listenLanguage: LaunchLanguageCode | null;
  assignmentGeneration: number;
  /**
   * `ListenerContext.workerHealth`, an input `RoutePolicyEngine` reads that had
   * no producer at all — the runtime pinned a literal because nothing upstream
   * could honestly observe the worker. It travels with the others because they
   * share a fate: the closed context has to be closed in ALL of them at once,
   * and a caller that read some from here and this one from somewhere else
   * could hold a context that was half retracted.
   */
  workerHealth: WorkerHealth;
}

/**
 * The CLOSED context — one frozen module-level object, shared by every closed
 * result.
 *
 * A fresh object per call would give the runtime's listener memo a new identity
 * on every store notification and rebuild the audio graph plan continuously. A
 * single instance also makes `toBe` identity the honest assertion for "still
 * closed".
 */
const CLOSED_LISTENER_CONTEXT: TranslationListenerContext = Object.freeze({
  listenLanguage: null,
  assignmentGeneration: 0,
  // The closed context reports `unavailable` WHATEVER the store holds. A viewer
  // who is not eligible to hear anything is not a viewer whose worker health
  // matters, and reporting a live `healthy` here would hand the policy engine a
  // half-retracted context — the exact shape this frozen object exists to
  // forbid.
  workerHealth: WORKER_HEALTH_FAIL_CLOSED,
});

/**
 * The viewer's audio context, or a CLOSED one.
 *
 * ⚠️ FAIL-CLOSED IS ALL-OR-NOTHING. When the viewer is not eligible this returns
 * the closed object entire — not the real generation with a null language.
 * `RoutePolicyEngine` checks the generation fence BEFORE it checks the
 * language, so handing back a real generation with no language would let a
 * caller that reads only the fence believe it held a current context. There is
 * one eligible shape and one closed shape, and nothing in between.
 *
 * Two conditions, and both are load-bearing:
 *  • the controller has spoken (`viewerContextKnown`) — zero placeholders are
 *    not a context, and admitting them is how a route becomes audible against a
 *    world nobody has described;
 *  • a language is actually assigned — `null` cannot equal any envelope's
 *    language, so it would be refused downstream anyway, but returning it as
 *    "eligible" would misreport the reason.
 */
// Typed on the ONE slice it reads, not the whole root state. A selector that
// demands sibling slices it never touches forces every caller — including a
// focused unit test — to build a store it does not need, which is how a test
// ends up describing a fictional shape.
export const selectTranslationListenerContext = createSelector(
  [
    (state: { translation: TranslationState }) => state.translation.viewerContextKnown,
    (state: { translation: TranslationState }) => state.translation.viewerListenLanguage,
    (state: { translation: TranslationState }) => state.translation.viewerAssignmentGeneration,
    (state: { translation: TranslationState }) => state.translation.viewerWorkerHealth,
  ],
  (known, listenLanguage, assignmentGeneration, workerHealth): TranslationListenerContext => {
    if (!known || listenLanguage === null) {
      return CLOSED_LISTENER_CONTEXT;
    }
    return { listenLanguage, assignmentGeneration, workerHealth };
  },
);

/**
 * The roster's relations as lookup maps, memoized so consumers rendering per
 * caption do not rebuild them per store notification.
 *
 * `null` when no verifiable roster is in force — the KNOWN-empty state.
 * Consumers must render that as `unknown` (badge the language, or say so),
 * never as "same language as me": an absent relation suppressing a caption a
 * reader needed is the own-language rule failing in the dangerous direction.
 */
export interface TranslationRosterView {
  bySpid: ReadonlyMap<string, TranslationRosterParticipant>;
  byLivekitIdentity: ReadonlyMap<string, TranslationRosterParticipant>;
  generation: number;
  /**
   * Room participants the endpoint could not verify (r14 finding 2). A view
   * with rows AND a non-zero count is a PARTIAL roster — consumers presenting
   * "each side" must say so, or the missing side reads as absent from the room.
   */
  unverifiedCount: number;
}

export const selectTranslationParticipantRoster = createSelector(
  [(state: { translation: TranslationState }) => state.translation.participantRoster],
  (roster): TranslationRosterView | null => {
    if (!roster.present) return null;
    const bySpid = new Map<string, TranslationRosterParticipant>();
    const byLivekitIdentity = new Map<string, TranslationRosterParticipant>();
    for (const row of roster.participants) {
      bySpid.set(row.recipient_id, row);
      byLivekitIdentity.set(row.livekit_identity, row);
    }
    return {
      bySpid,
      byLivekitIdentity,
      generation: roster.generation,
      unverifiedCount: roster.unverified_count,
    };
  },
);

/**
 * The speaker↔reader relation for every bubble — the caption polarity input
 * (design review findings 4 + 6, via the pure `resolveCaptionRelation`).
 *
 * Computed from the AUTHORITATIVE lineage first (final bubbles' resolved
 * turns: spid disagreement refuses to classify; per-utterance
 * `detected_language` outranks the claim and the roster), then the RETAINED
 * server-verified identity map for interims. Never from `isSelf`, never from
 * role heuristics, never from a raw self-writable attribute.
 *
 * ⚠️ NOT gated on translation-display activity, deliberately: the overlay
 * needs a KNOWN-different relation even when the display is inactive — that
 * is exactly the state in which the old code leaked source (finding 5).
 */
export const selectCaptionRelations = createSelector(
  [selectTranscriptionBubbles, selectTranslationSlice],
  (
    bubbles: TranscriptionBubble[],
    translation: TranslationState,
  ): ReadonlyMap<string, CaptionRelationResult> => {
    const relations = new Map<string, CaptionRelationResult>();
    // The matrix's language, not just the text preference (r3 finding 2).
    const viewerLanguage =
      translation.targetLanguage ??
      translation.viewerListenLanguage ??
      translation.captionListenLanguageMemory;
    const retained = Object.entries(translation.rosterRetainedByIdentity);
    const rosterByIdentity = new Map<string, TranslationRosterParticipant>(
      retained.map(([k, v]) => [k, v]),
    );
    const rosterBySpid = new Map<string, TranslationRosterParticipant>(
      retained.map(([, v]) => [v.recipient_id, v]),
    );
    for (const bubble of bubbles) {
      const lineage = resolveBubbleLineage(translation, bubble);
      const relation = resolveCaptionRelation({
        viewerLanguage,
        turns: lineage.turns,
        turnsCoverBubble: lineage.unresolved_message_count === 0,
        uncoveredTailIsInterimOnly: lineage.unresolved_final_count === 0,
        speakerIdentity: bubble.speaker || null,
        rosterByIdentity,
        rosterBySpid,
      });
      if (relation.coveredOnly === true) {
        const correlatedSyncIds = new Set(
          lineage.correlations.map((entry) => entry.transcript_sync_id),
        );
        const seen = new Set<string>();
        const coveredMessages = bubble.messages.filter((message) => {
          if (!message.isFinal || !message.syncId) return false;
          if (!correlatedSyncIds.has(message.syncId) || seen.has(message.syncId)) return false;
          seen.add(message.syncId);
          return true;
        });
        const coveredSourceText = coveredMessages
          .map((message) => message.message)
          .join(' ')
          .trim();
        relations.set(bubble.bubbleId, {
          ...relation,
          coveredSourceText,
          coveredSourceUnits: coveredMessages.length,
        });
      } else {
        relations.set(bubble.bubbleId, relation);
      }
    }
    return relations;
  },
);

/**
 * Is a translation regime in force for this viewer's TEXT? The polarity
 * matrix runs fail-closed (unknown → pending) only while this is true; the
 * KNOWN-different half of the matrix applies regardless (see
 * `lib/translation/captionPolarity.ts`).
 */
/**
 * The language the caption matrix defends: the TEXT preference when chosen,
 * else the viewer's ASSIGNED listen language (r3 finding 2 — a reader who
 * turned the text display Off still HAS a language, and a Mandarin caption is
 * as unreadable to them with the display off as on). Null only when the
 * platform knows NO language for this reader at all — the ordinary
 * untranslated call.
 */
export const selectCaptionReaderLanguage = createSelector(
  [selectTranslationSlice],
  (translation: TranslationState): LaunchLanguageCode | null =>
    translation.targetLanguage ??
    translation.viewerListenLanguage ??
    // r4 finding 2: invalidation clears the CURRENT listen language so audio
    // fails closed, but the reader did not forget their language because a
    // lease expired — the caption matrix stays armed on the call's memory.
    translation.captionListenLanguageMemory,
);

export const selectCaptionRegimeActive = createSelector(
  [selectCaptionReaderLanguage],
  // READER-LANGUAGE-ONLY, deliberately NOT route-gated (r2 finding 1): routes
  // land seconds after a foreign caller can already be speaking, and a
  // viewer-context loss clears routes without clearing the preference — in
  // both windows the reader still HAS a language and the matrix must stay
  // armed. No known language at all = no own-language to enforce toward.
  (readerLanguage): boolean => readerLanguage !== null,
);

/**
 * Routes for one language that are addressed to this viewer, could carry text to
 * a person, and are generation-current — optionally narrowed to a set of states.
 *
 * The single place the eligibility rules live. The `media_purpose` filter is the
 * same one `selectGoverningRoute` applies, and it has to be: an `avatar_output`
 * or `ai_input_mix` route can legitimately be addressed to this recipient in this
 * language while delivering nothing a person reads. `isGenerationCurrent` closes
 * BI-11: a route minted before this viewer's assignment moved describes a world
 * that no longer exists.
 */
function hasViewerRouteForLanguage(
  translation: TranslationState,
  language: LaunchLanguageCode,
  allowedStates?: ReadonlySet<RouteState>,
): boolean {
  // Eligibility starts with the VIEWER, not the route. An unknown context
  // disqualifies every route at once, and it is invisible to the generation
  // fence below because the fence's own default is the zero it would compare
  // against.
  if (!viewerContextIsEffective(translation)) return false;
  return Object.values(translation.routes).some(
    (route) =>
      route.target_language === language &&
      (LISTENER_PURPOSES as readonly string[]).includes(route.media_purpose) &&
      addressesViewer(translation, route) &&
      isGenerationCurrent(translation, route) &&
      (allowedStates === undefined || allowedStates.has(route.state)),
  );
}

/**
 * True when a route for the chosen language is addressed to this viewer, IN ANY
 * STATE.
 *
 * ⚠️ This is NOT a confirmation predicate and must never be used as one — for
 * "is this language in force" use {@link isTranslationLanguageEstablished}.
 *
 * It deliberately ignores `route.state`, because it gates whether the bilingual
 * DISPLAY exists at all. Narrowing it to deliverable states would switch the
 * whole display off the moment a route went `unavailable`, taking the
 * "Translation unavailable" marker off the screen with it — the reader would see
 * nothing rather than being told. That is exactly the silent degradation the
 * five states exist to prevent.
 */
export function isTranslationDisplayActive(translation: TranslationState): boolean {
  const language = translation.targetLanguage;
  if (language === null) return false;
  return hasViewerRouteForLanguage(translation, language);
}

export const selectTranslationDisplayActive = (state: TranslationRootState): boolean =>
  isTranslationDisplayActive(state.translation);

/**
 * Can a route for this language still produce output for this viewer?
 *
 * Answers "keep waiting, or surface a failure". False means every route this
 * viewer has for the language is terminal (`unavailable`) or over (`ended`).
 * A merely `pending` route is deliverable: it has not failed, it just has not
 * started.
 */
export function isTranslationLanguageDeliverable(
  translation: TranslationState,
  language: LaunchLanguageCode,
): boolean {
  return hasViewerRouteForLanguage(translation, language, DELIVERABLE_ROUTE_STATES);
}

/**
 * Has delivery of this language to this viewer actually BEGUN?
 *
 * This is what a confirmation display must use. It is strictly narrower than
 * {@link isTranslationLanguageDeliverable}: a `pending` route is deliverable but
 * NOT established, so a requested language is never shown as the language in
 * force until a route has moved past assignment.
 */
export function isTranslationLanguageEstablished(
  translation: TranslationState,
  language: LaunchLanguageCode,
): boolean {
  return hasViewerRouteForLanguage(translation, language, ESTABLISHED_ROUTE_STATES);
}

/**
 * Has THIS CALL been confirmed to be translating for a person?
 *
 * THE QUIET GATE. Owner, 2026-08-27: "translation is still enabled when both
 * agents appear to be speaking the same language.... we should always assume
 * they are speaking english and hide all these notifications, events, etc about
 * translation inside the transcript. Once we confirm the language, we should
 * then be a but more visual about the translation features... 99% of calls will
 * be english-english."
 *
 * The confirming fact is the owner's OWN earlier rule for the floating badge — a
 * LIVE human-delivery translation ROUTE, because assignments only PLAN
 * translation (sol r32 finding 3) — but LATCHED for the call.
 * `observedEstablishments` already IS that latch and nothing else in the store
 * is: `observeEstablishment` admits only `LISTENER_PURPOSES` in
 * `ESTABLISHED_ROUTE_STATES` carrying a non-null target language, the map is
 * accumulate-only, it deliberately survives a remount, and `resetCallScopedState`
 * is the only thing that empties it.
 *
 * LATCHED ON PURPOSE. Un-confirming when a route goes `unavailable` would take
 * the "Translation unavailable" marker off the screen along with the rest of the
 * chrome, so the reader would be shown nothing instead of being told — exactly
 * the silent degradation the five route states exist to prevent. Once a call has
 * delivered translation to a person it stays a visually translating call until
 * the call ends.
 *
 * The recipient clause mirrors `routesShowActiveTranslation`: a listener-purpose
 * observation with no concrete human recipient and no `all_participants` group
 * is plumbing, and plumbing must not light the UI.
 */
/**
 * The two facts that TOGETHER confirm a cross-language call, as separate
 * predicates — because production never puts them on one route (sol r58
 * finding 1): a route with a KNOWN source language is always a
 * `translated_source` addressed to `recipient_group='translation_pipeline'`
 * (the DB constraint in migration 20261091 forbids addressing it to a
 * person), while the human-addressed route is always a `recipient_mix`,
 * which deliberately carries a NULL source language because a complete mix
 * has no single one. Requiring both facts on ONE observation therefore
 * confirmed nothing, ever — the opposite failure of the en→en bug this gate
 * exists to fix.
 *
 * So the gate is a CORRELATED pair over the latch (sol r59 finding 1 — two
 * independent existentials were not enough):
 *  - somebody is RECEIVING (a human-addressed established delivery), and
 *  - something genuinely BEING TRANSLATED feeds THAT person's language: an
 *    established route with a known source differing from its target, whose
 *    TARGET equals the human delivery's target. The worker also mints
 *    analysis-only cross-language producers with no recipients at all
 *    (route_planner's `recipient_ids=()` sessions); an analysis producer's
 *    target matches no human delivery, so it can never light the chrome for
 *    people who all share one language.
 * An en→en call has the human mix and no producer; a zh→en call has the
 * officer's en mix AND a zh→en producer — matched. An observation whose
 * source is unknown proves nothing — "we do not know" is not
 * "cross-language" (the BI-13 polarity rule). `?? null` keeps records
 * observed before the field existed from vacuously confirming.
 */
function observationIsHumanAddressed(observation: ObservedEstablishment): boolean {
  return observation.recipient_id !== null || observation.recipient_group === 'all_participants';
}

function observationShowsCrossLanguage(observation: ObservedEstablishment): boolean {
  const source = observation.source_language ?? null;
  return source !== null && source !== observation.target_language;
}

/**
 * The producer targets that actually FEED a person: cross-language
 * establishments whose target language some human-addressed establishment
 * carries. Empty when the call is not confirmed.
 */
/**
 * Judge human↔producer pairs NOW, while both intervals are live, and LATCH
 * the verdict on the producer (sol r62 finding 1). Runs on every route
 * envelope, so a pair is detected at whichever establishment completes it;
 * later interval rewrites (a reconnect erases and restarts the human's
 * interval) cannot dissolve a pair that genuinely happened.
 */
function sweepConfirmingPairs(state: TranslationState): void {
  const observations = Object.values(state.observedEstablishments);
  const humans = observations.filter(observationIsHumanAddressed);
  if (humans.length === 0) return;
  for (const producer of observations) {
    if (producer.paired_confirming === true) continue;
    if (!observationShowsCrossLanguage(producer)) continue;
    if (
      humans.some(
        (human) =>
          human.target_language === producer.target_language && intervalsOverlap(human, producer),
      )
    ) {
      producer.paired_confirming = true;
    }
  }
}

function intervalsOverlap(a: ObservedEstablishment, b: ObservedEstablishment): boolean {
  // Legacy records (observed before the interval fields existed) read as
  // order 0 / never-ended, which preserves the pre-interval behaviour.
  const aStart = a.observed_at_order ?? 0;
  const bStart = b.observed_at_order ?? 0;
  const aEnd = a.ended_at_order ?? null;
  const bEnd = b.ended_at_order ?? null;
  return (aEnd === null || bStart < aEnd) && (bEnd === null || aStart < bEnd);
}

function confirmingProducerObservations(
  observations: readonly ObservedEstablishment[],
): ObservedEstablishment[] {
  // The latched verdicts, not a fresh derivation: pairing was judged in the
  // reducer while both intervals were live (sweepConfirmingPairs), so a
  // later interval rewrite cannot un-confirm what genuinely overlapped.
  return observations.filter((observation) => observation.paired_confirming === true);
}

export function isTranslationCrossLanguageConfirmed(translation: TranslationState): boolean {
  return (
    confirmingProducerObservations(Object.values(translation.observedEstablishments)).length > 0
  );
}

export const selectTranslationCrossLanguageConfirmed = (state: TranslationRootState): boolean =>
  isTranslationCrossLanguageConfirmed(state.translation);

/** One frozen empty array, so "no languages" is a STABLE reference. */
const EMPTY_LANGUAGE_LIST: LaunchLanguageCode[] = Object.freeze(
  [],
) as unknown as LaunchLanguageCode[];

/**
 * The distinct languages this confirmed call spans — the material the ONE reveal
 * notification names ("Live translation enabled: 中文 ↔ English").
 *
 * Empty until {@link isTranslationCrossLanguageConfirmed}, so a caller cannot
 * name languages for a call that has not been confirmed to translate. The
 * verified roster's SPOKEN languages are the primary source because they are
 * what the two people are actually saying; the established target languages are
 * folded in so a call whose roster row has not landed yet still names both sides
 * rather than one.
 */
export const selectTranslationConfirmedLanguages = createSelector(
  [
    (state: TranslationRootState) => state.translation.observedEstablishments,
    (state: TranslationRootState) => state.translation.participantRoster,
  ],
  // Memoized because it BUILDS an array: an unmemoized selector would hand
  // `useSyncExternalStore` a fresh reference on every store change and re-render
  // every consumer forever.
  (observedEstablishments, participantRoster): LaunchLanguageCode[] => {
    const observations = Object.values(observedEstablishments);
    const confirming = confirmingProducerObservations(observations);
    if (confirming.length === 0) return EMPTY_LANGUAGE_LIST;
    const codes = new Set<LaunchLanguageCode>();
    if (participantRoster.present) {
      for (const row of participantRoster.participants) {
        if (row.spoken_language !== null) codes.add(row.spoken_language);
      }
    }
    // Languages come from the verified roster and from the gate's own
    // matched pairs — never from the raw route map (sol r57 finding 6: a
    // PENDING route could inject a stray third code and force the tabs
    // generic), and not from unmatched producers either (sol r59 finding 1:
    // an analysis-only producer feeds nobody, so its languages are not what
    // any person is reading):
    //  - a human-addressed delivery names its TARGET (a recipient_mix has no
    //    single source);
    //  - a MATCHED cross-language producer names BOTH sides — real
    //    translation activity someone is receiving, so its source is a real
    //    call language even before the roster row lands.
    // The no-shrink property the old routes sweep defended survives, because
    // the latch is accumulate-only for the life of the call.
    for (const observation of observations) {
      if (observationIsHumanAddressed(observation)) {
        codes.add(observation.target_language);
      }
    }
    for (const observation of confirming) {
      codes.add(observation.target_language);
      const source = observation.source_language ?? null;
      if (source !== null) codes.add(source);
    }
    return Array.from(codes);
  },
);

function addressesViewer(
  translation: TranslationState,
  route: { recipient_id: string | null; recipient_group: RecipientGroup | null },
): boolean {
  if (route.recipient_group === 'all_participants') return true;
  return (
    translation.viewerRecipientId !== null && route.recipient_id === translation.viewerRecipientId
  );
}

/**
 * BI-11. A route minted before this viewer's language assignment moved
 * describes a world that no longer exists, so it may not drive the display
 * however high its `route_generation` is. Same predicate as the approved
 * `RoutePolicyEngine.ineligibleReason`.
 */
function isGenerationCurrent(
  translation: TranslationState,
  route: { assignment_generation: number },
): boolean {
  return route.assignment_generation >= translation.viewerAssignmentGeneration;
}

/**
 * The route that governs one turn's translation into one language, or undefined.
 *
 * Order matters. A source-bound `translated_source` route names the exact
 * `(source_participant_id, source_epoch)` it carries, so it is the precise
 * answer; the viewer's `recipient_mix` is not source-bound (`source_epoch` is
 * null for it under the route contract) and legitimately governs every turn for
 * that viewer and language. Anything addressed to another recipient, carrying
 * another language, or bound to a SUPERSEDED epoch is excluded by construction
 * and therefore cannot make this turn look delayed or unavailable. Absence of a
 * route is not a fault signal.
 */
function selectGoverningRoute(
  translation: TranslationState,
  turn: TranslationSourceTurnRef,
  targetLanguage: LaunchLanguageCode,
): RouteEnvelopeV1 | undefined {
  let sourceBound: RouteEnvelopeV1 | undefined;
  let mix: RouteEnvelopeV1 | undefined;

  // Same viewer-first rule as `hasViewerRouteForLanguage`. No route governs a
  // turn for a viewer whose context is unknown.
  if (!viewerContextIsEffective(translation)) return undefined;

  for (const route of Object.values(translation.routes)) {
    if (route.call_id !== turn.call_id) continue;
    if (route.target_language !== targetLanguage) continue;
    if (!(LISTENER_PURPOSES as readonly string[]).includes(route.media_purpose)) continue;
    if (!addressesViewer(translation, route)) continue;
    // BI-11: a stale assignment generation disqualifies a route outright,
    // whatever its `route_generation` is.
    if (!isGenerationCurrent(translation, route)) continue;

    if (
      route.media_purpose === 'translated_source' &&
      route.source_participant_id === turn.stable_participant_id &&
      route.source_epoch === turn.source_epoch
    ) {
      if (!sourceBound || route.route_generation > sourceBound.route_generation)
        sourceBound = route;
      continue;
    }
    if (
      route.media_purpose === 'recipient_mix' &&
      translation.viewerRecipientId !== null &&
      route.recipient_id === translation.viewerRecipientId
    ) {
      if (!mix || route.route_generation > mix.route_generation) mix = route;
    }
  }

  return sourceBound ?? mix;
}

/**
 * The visible badges for one turn. Pure, exported, and the single definition
 * both surfaces use — the transcript bubble and the caption overlay cannot
 * disagree about what `delayed` means because neither of them decides it.
 *
 * The retained text is NOT an input: text renders whenever it exists, under any
 * badge. That is what makes "already translated, and the route is reconnecting"
 * representable at all.
 */
export function translationTurnBadges(view: {
  content: TranslationContentStatus;
  route_condition: TranslationRouteCondition;
  revised: boolean;
}): TranslationBadge[] {
  if (view.content === 'source_is_target') return [];

  const badges: TranslationBadge[] = [];
  if (view.content === 'unavailable') {
    badges.push('unavailable');
  } else if (view.content === 'translated') {
    // A finished translation on a closed route is a normal end of call, so
    // `closed` produces no badge here. A live fault still does.
    if (
      view.route_condition === 'delayed' ||
      view.route_condition === 'reconnecting' ||
      view.route_condition === 'unavailable'
    ) {
      badges.push(view.route_condition);
    }
  } else if (view.route_condition === 'closed' || view.route_condition === 'unavailable') {
    // Nothing arrived and the route will not produce it. Continuing to show
    // "translating" for the rest of the call is the silent degradation the five
    // states exist to prevent.
    badges.push('unavailable');
  } else if (view.route_condition === 'delayed' || view.route_condition === 'reconnecting') {
    badges.push(view.route_condition);
  } else {
    badges.push('translating');
  }

  if (view.revised) badges.push('revised');
  return badges;
}

export function buildTurnView(
  translation: TranslationState,
  sourceTurnId: string,
  targetLanguage: LaunchLanguageCode,
  /**
   * The turn's source text as the TRANSCRIPT holds it (the join input for the
   * merged record). `null` when the caller has no transcript in view (a quote
   * view): then `merged_text` is null — the slice itself holds no source text.
   */
  sourceText: string | null = null,
): TranslationTurnView | null {
  const turn = translation.turns[sourceTurnId];
  if (!turn) return null;

  const key = pairKey(sourceTurnId, targetLanguage);
  const history = translation.revisions[key] ?? [];
  const selection = translation.selections[key];
  const selected = selection
    ? history.find((entry) => entry.translation_revision_id === selection.selected_revision_id)
    : undefined;
  const marker = translation.unavailable[key];
  const route = selectGoverningRoute(translation, turn, targetLanguage);
  const merged = mergedRecordFor(translation, turn, targetLanguage, selected ?? null, sourceText);

  // BI-13: exact string equality. `languageEquals` is false when either side is
  // null, so an unknown source language never claims to already be the target —
  // "we do not know" is not "no translation needed".
  const content: TranslationContentStatus = languageEquals(turn.source_language, targetLanguage)
    ? 'source_is_target'
    : selected
      ? 'translated'
      : marker
        ? 'unavailable'
        : 'awaiting_translation';

  return {
    source_turn_id: sourceTurnId,
    target_language: targetLanguage,
    text: selected ? selected.text : null,
    content,
    route_condition: route ? ROUTE_STATE_TO_CONDITION[route.state] : 'ok',
    revised: selection ? selection.selection_change_count > 0 : false,
    translation_revision_id: selected ? selected.translation_revision_id : null,
    revision_index: selected ? selected.revision_index : null,
    revision_count: history.length,
    delivery_state: selected ? selected.delivery_state : null,
    unavailable_reason: marker ? marker.reason_code : null,
    nuance_annotations: selected ? selected.nuance_annotations : [],
    heard_revision_id: translation.heardRevisionId[key] ?? null,
    origin: merged.origin,
    request_contract_version: merged.request_contract_version,
    merged_text: merged.merged_text,
    merged_text_attested: merged.attested,
    attachment: selected ? revisionAttachmentOf(selected) : null,
    settlement: selected ? settlementOfRecord(selected) : null,
  };
}

/**
 * A selected revision is PROVISIONAL when its worker attachment says so:
 * `attachment_confidence !== 'exact'` (sol). A null confidence — a row from
 * before the provenance columns, or a legacy 19-key record — is UNKNOWN, and
 * unknown is never guessed into provisional.
 */
function isProvisionalAttachment(attachment: RevisionAttachmentV1 | null | undefined): boolean {
  return (
    attachment !== null &&
    attachment !== undefined &&
    attachment.attachment_confidence !== null &&
    attachment.attachment_confidence !== 'exact'
  );
}

/**
 * THE MERGED RECORD for one pair (owner design 2026-09-02, "One Transcript,
 * Two Ears"): "<source_text> [<translation_text>]" with its origin.
 *
 * Two shapes, both served:
 *   - the projection RPC ATTESTS the record (P3's additive `merged` rows) —
 *     used when its `translation_text` is the revision currently selected
 *     (identical bytes), so an attestation taken before a re-selection can
 *     never put superseded words on screen;
 *   - otherwise the record is COMPOSED locally from the turn's text and the
 *     selected revision, by the one shared composition.
 *
 * The origin comes from the selected row's own `request_contract_version`
 * first; the attestation's origin fills in only when the row is silent.
 */
export interface TranslationMergedRecord {
  source_turn_id: string;
  target_language: LaunchLanguageCode;
  /** The transcript's source text for the turn; `null` when none is in view. */
  source_text: string | null;
  translation_text: string | null;
  /** The composition; `null` only when `source_text` is null. */
  merged_text: string | null;
  origin: TranslationOrigin | null;
  request_contract_version: string | null;
  /** True when the RPC attested this pair for the revision on screen (its origin may fill in). */
  attested: boolean;
}

function mergedRecordFor(
  translation: TranslationState,
  turn: TranslationSourceTurnRef,
  targetLanguage: LaunchLanguageCode,
  selected: TranslationRevisionV1 | null,
  sourceText: string | null,
): TranslationMergedRecord {
  const key = pairKey(turn.source_turn_id, targetLanguage);
  const attestation = translation.mergedRecords[key];
  const translationText = selected ? selected.text : null;
  const rowOrigin = selected ? translationOriginFromContract(selected.request_contract_version) : null;
  const usable = attestation !== undefined && attestation.translation_text === translationText;
  return {
    source_turn_id: turn.source_turn_id,
    target_language: targetLanguage,
    source_text: sourceText,
    translation_text: translationText,
    merged_text: sourceText === null ? null : composeMergedRecord(sourceText, translationText),
    origin: rowOrigin ?? (usable ? attestation.origin : null),
    request_contract_version:
      (selected ? selected.request_contract_version : null) ??
      (usable ? attestation.request_contract_version : null),
    attested: usable,
  };
}

/**
 * The turn's source text as the TRANSCRIPT holds it: the final messages whose
 * authoritative sync identity correlates to the turn, in message order. The
 * translation slice keeps no source text of its own, so the merged record is
 * a JOIN — the same join `resolveBubbleLineage` makes, read the other way.
 */
export function transcriptSourceTextForTurn(
  translation: TranslationState,
  messages: readonly TranscriptionMessage[],
  sourceTurnId: string,
): string | null {
  const texts: string[] = [];
  for (const message of messages) {
    if (!message.isFinal || !carriesJoinableIdentity(message)) continue;
    const syncId = message.syncId;
    if (syncId === undefined || translation.ambiguousTranscriptSyncIds[syncId]) continue;
    if (translation.sourceTurnIdByTranscriptSyncId[syncId] !== sourceTurnId) continue;
    if (message.message.length > 0) texts.push(message.message);
  }
  return texts.length > 0 ? texts.join(' ') : null;
}

/** Reduce the turns' origins to the bubble's (see {@link BubbleTranslationView.origin}). */
function reduceOrigin(turns: readonly TranslationTurnView[]): TranslationOrigin | null {
  const translated = turns.filter((turn) => turn.content === 'translated');
  if (translated.length === 0) return null;
  if (translated.some((turn) => turn.origin === 'fallback')) return 'fallback';
  if (translated.every((turn) => turn.origin === 'engine')) return 'engine';
  return null;
}

function reduceBadges(turns: readonly TranslationTurnView[]): TranslationBadge[] {
  const perTurn = turns.map((turn) => translationTurnBadges(turn));
  const operational = OPERATIONAL_BADGE_PRECEDENCE.find((candidate) =>
    perTurn.some((badges) => badges.includes(candidate)),
  );
  const badges: TranslationBadge[] = [];
  if (operational) badges.push(operational);
  if (turns.some((turn) => turn.revised)) badges.push('revised');
  return badges;
}

function summariseContent(turns: readonly TranslationTurnView[]): TranslationContentStatus {
  if (turns.some((turn) => turn.content === 'unavailable')) return 'unavailable';
  if (turns.some((turn) => turn.content === 'awaiting_translation')) return 'awaiting_translation';
  return 'translated';
}

/**
 * Display order within a bubble. This is NOT the canonical hashed order — that
 * lives in the approved `bilingualProjection.ts` and is deliberately not
 * duplicated here, because a second copy of a hashed ordering rule is a drift
 * hazard. Nothing computed in this module feeds a hash.
 */
function compareTurnRefs(a: TranslationSourceTurnRef, b: TranslationSourceTurnRef): number {
  if (a.started_at_ms !== b.started_at_ms) return a.started_at_ms - b.started_at_ms;
  if (a.source_epoch !== b.source_epoch) return a.source_epoch - b.source_epoch;
  if (a.ordinal !== b.ordinal) return a.ordinal - b.ordinal;
  if (a.source_turn_id === b.source_turn_id) return 0;
  return a.source_turn_id < b.source_turn_id ? -1 : 1;
}

export interface TranscriptTurnCorrelation {
  transcript_sync_id: string;
  source_turn_id: string;
}

export interface BubbleLineage {
  /** The turns an authoritative correlation resolved, in display order. */
  turns: TranslationSourceTurnRef[];
  /**
   * The exact correlations that produced {@link turns}. A quote records these so
   * it can REVALIDATE later: a correlation that is subsequently contradicted
   * must stop the quote presenting a translation that has been disproven.
   */
  correlations: TranscriptTurnCorrelation[];
  /**
   * Distinct messages in this bubble that CARRY an authoritative identity.
   * Messages with none are absent from this count and present in
   * {@link unresolved_message_count} — they are unresolved, not invisible.
   */
  identified_message_count: number;
  /**
   * How many of those have NO usable correlation. Non-zero means the bubble's
   * translation covers only PART of what was said, and every surface must say
   * so — a quote that silently covers half a turn is an evidence defect, which
   * is the failure this whole feature exists to prevent.
   */
  unresolved_message_count: number;
  /**
   * The FINAL portion of {@link unresolved_message_count}: displayed finals
   * with no usable correlation. The caption polarity distinguishes them from
   * an in-flight interim tail — an unresolved FINAL is settled material of
   * unproven language and blocks a covered-source render, while an interim is
   * merely not-yet-proven and may be WITHHELD while the proven finals show
   * (the "Translating…"-over-own-speech complaint, 2026-08-24).
   */
  unresolved_final_count: number;
}

/**
 * Lineage for a bubble, resolved ONLY through stated correlations.
 *
 * Nothing is inferred from the segment id. A message whose sync identity has no
 * correlation, or an ambiguous one, is counted as unresolved and reported —
 * never skipped quietly, and never resolved by picking the single turn that
 * happens to claim its segment id today (see the header).
 */
/**
 * The de-dup key for a message carrying NO authoritative identity.
 *
 * Used only to fold an interim stream into its own final. It is never a join
 * key and never shares a set with a sync identity — see `resolveBubbleLineage`.
 */
function unidentifiedDedupeKey(message: TranscriptionMessage): string {
  // KIND-TAGGED. Untagged values let different kinds collide with each other:
  // an interim keyed by `segmentId: "x"` would fold into an unrelated final
  // keyed by `segmentKey: "x"`, and a segment id shaped like `ts:<n>` would
  // collide with the timestamp fallback. The separator is U+0000, written as an
  // escape so the byte stays visible in review; a tag cannot contain it.
  if (message.segmentId) return `segment-id\u0000${message.segmentId}`;
  if (message.segmentKey) return `segment-key\u0000${message.segmentKey}`;
  return `timestamp\u0000${message.timestamp}`;
}

/**
 * True when this message carries an identity that may be JOINED.
 *
 * `transcriptSyncIdentity` synthesises a derived identity for any message the
 * producer did not identify, so `syncId` is almost never absent in production —
 * it is present and non-authoritative. Joinability must therefore be decided
 * BEFORE any bookkeeping: deduplicating on a derived identity merged two finals
 * the reader was shown separately, because both derive the same string.
 */
function carriesJoinableIdentity(message: TranscriptionMessage): boolean {
  return isJoinableTranscriptIdentity(message.syncId);
}

export function resolveBubbleLineage(
  translation: TranslationState,
  bubble: TranscriptionBubble,
): BubbleLineage {
  const refs: TranslationSourceTurnRef[] = [];
  const correlations: TranscriptTurnCorrelation[] = [];
  const seenTurns = new Set<string>();
  // DISJOINT NAMESPACES. Sync identities and the de-dup keys for unidentified
  // messages are different kinds of identifier from different issuers, and
  // nothing stops one call's sync id being byte-identical to another message's
  // segment id. Sharing one set let such a collision silently swallow the
  // unidentified message: the count stayed 0, the bubble read complete, and the
  // pin guard accepted it.
  const seenSyncIds = new Set<string>();
  const seenInterimKeys = new Set<string>();
  let unresolved = 0;
  let unresolvedFinals = 0;
  let identified = 0;

  // Segments whose FINAL is present. An interim for one of those is superseded
  // by its final and is not separately displayed material.
  const finalisedUnidentifiedKeys = new Set<string>();
  for (const message of bubble.messages) {
    if (carriesJoinableIdentity(message) || !message.isFinal) continue;
    finalisedUnidentifiedKeys.add(unidentifiedDedupeKey(message));
  }

  for (const message of bubble.messages) {
    // `syncId` is the transcript pipeline's own identity for the message.
    // `segmentKey` may be synthesised from a speaker and a time bucket, and
    // `segmentId` is a process-local counter; neither is a lineage identity.
    const syncId = message.syncId;
    // Joinability FIRST, before any de-dup. A derived identity is not a key.
    if (!carriesJoinableIdentity(message)) {
      // A displayed message with no AUTHORITATIVE identity is UNRESOLVED, not
      // absent — whether it carries no identity or a locally-derived one.
      // Skipping it silently was how a half-covered bubble still reported
      // itself complete.
      const dedupe = unidentifiedDedupeKey(message);
      if (message.isFinal) {
        // EVERY final counts. Two distinct finals that happen to share a
        // process-local segment id are two things the reader was shown, and
        // merging them under-counts what the translation fails to cover.
        unresolved += 1;
        unresolvedFinals += 1;
        continue;
      }
      // An interim is one evolving message. It is displayed material only while
      // its own final has not arrived.
      if (finalisedUnidentifiedKeys.has(dedupe) || seenInterimKeys.has(dedupe)) continue;
      seenInterimKeys.add(dedupe);
      unresolved += 1;
      continue;
    }
    if (syncId === undefined || seenSyncIds.has(syncId)) continue;
    seenSyncIds.add(syncId);
    identified += 1;

    if (translation.ambiguousTranscriptSyncIds[syncId]) {
      unresolved += 1;
      if (message.isFinal) unresolvedFinals += 1;
      continue;
    }
    const sourceTurnId = translation.sourceTurnIdByTranscriptSyncId[syncId];
    const ref = sourceTurnId ? translation.turns[sourceTurnId] : undefined;
    if (!ref) {
      unresolved += 1;
      if (message.isFinal) unresolvedFinals += 1;
      continue;
    }
    correlations.push({ transcript_sync_id: syncId, source_turn_id: ref.source_turn_id });
    if (seenTurns.has(ref.source_turn_id)) continue;
    seenTurns.add(ref.source_turn_id);
    refs.push(ref);
  }

  refs.sort(compareTurnRefs);
  return {
    turns: refs,
    correlations,
    identified_message_count: identified,
    unresolved_message_count: unresolved,
    unresolved_final_count: unresolvedFinals,
  };
}

export function buildBubbleTranslationView(
  translation: TranslationState,
  bubble: TranscriptionBubble,
  targetLanguage: LaunchLanguageCode,
): BubbleTranslationView | null {
  const lineage = resolveBubbleLineage(translation, bubble);
  const refs = lineage.turns;

  const turns: TranslationTurnView[] = [];
  for (const ref of refs) {
    const view = buildTurnView(
      translation,
      ref.source_turn_id,
      targetLanguage,
      transcriptSourceTextForTurn(translation, bubble.messages, ref.source_turn_id),
    );
    // A turn already spoken in the target language is owed no translation, so it
    // contributes no line rather than an empty one.
    if (view && view.content !== 'source_is_target') turns.push(view);
  }

  const lineageComplete = lineage.unresolved_message_count === 0;

  if (turns.length === 0) {
    // Every resolved turn was already in the target language AND nothing is
    // outstanding: no translation is owed and no line is rendered.
    if (refs.length > 0 && lineageComplete) return null;
    // Nothing resolved. Translation IS running for this viewer, so the bubble is
    // owed one and says so, rather than silently showing nothing.
    return {
      bubble_id: bubble.bubbleId,
      target_language: targetLanguage,
      source_language: null,
      text: null,
      content: 'awaiting_lineage',
      badges: ['translating'],
      turns: [],
      turn_count: 0,
      translated_turn_count: 0,
      revised_turn_count: 0,
      awaiting_turn_count: 0,
      unavailable_turn_count: 0,
      lineage_complete: lineageComplete,
      unresolved_message_count: lineage.unresolved_message_count,
      origin: null,
      provisional: false,
    };
  }

  const texts = turns
    .map((turn) => turn.text)
    .filter((text): text is string => text !== null && text.length > 0);

  const badges = reduceBadges(turns);
  // Part of what was said has no lineage yet, so the translation below the
  // source covers only part of it. Retain everything that DID resolve and
  // declare the gap; dropping the badge would present a partial translation as
  // a whole one.
  if (!lineageComplete && !badges.includes('translating')) {
    badges.unshift('translating');
  }

  const spokenLanguages = new Set<LaunchLanguageCode>();
  for (const turn of turns) {
    const spoken = translation.turns[turn.source_turn_id]?.source_language ?? null;
    if (spoken !== null) spokenLanguages.add(spoken);
  }

  return {
    bubble_id: bubble.bubbleId,
    target_language: targetLanguage,
    source_language: spokenLanguages.size === 1 ? [...spokenLanguages][0] : null,
    text: texts.length > 0 ? texts.join(' ') : null,
    content: summariseContent(turns),
    badges,
    turns,
    turn_count: turns.length,
    translated_turn_count: turns.filter((turn) => turn.content === 'translated').length,
    revised_turn_count: turns.filter((turn) => turn.revised).length,
    awaiting_turn_count: turns.filter((turn) => turn.content === 'awaiting_translation').length,
    unavailable_turn_count: turns.filter((turn) => turn.content === 'unavailable').length,
    lineage_complete: lineageComplete,
    unresolved_message_count: lineage.unresolved_message_count,
    origin: reduceOrigin(turns),
    // D4c: the wire's settlement decides when present; the attachment
    // inference is the fallback for a record from an older producer.
    provisional: turns.some(
      (turn) =>
        turn.content === 'translated' &&
        (turn.settlement != null
          ? turn.settlement === 'provisional'
          : isProvisionalAttachment(turn.attachment)),
    ),
  };
}

/**
 * THE DEDUPE REGISTRY (sol verdict on board run 23, 2026-09-02): every pair's
 * SELECTED revision that carries an engine range, keyed by its COORDINATE
 * SPACE and ordered by [start, end). The overlay subtracts these intervals
 * from a row's provisional text so an interval a LATER row settled — and
 * renders — leaves the earlier row: rendered exactly once, never nowhere.
 *
 * LEGACY BUCKET (board run 24): `allocation_group_id` when the worker minted one
 * (a joint allocation), else the ledger's ABSOLUTE engine stream, which the
 * browser identifies as (target language, source participant, source epoch)
 * — `engineCoordinateSpaceKey`. On the wire the group id is NULL for every
 * single-member commit and every corrective row, so keying by group alone
 * left the registry EMPTY for the ordinary call and the dedupe inert. Grouped
 * ranges also carry absolute offsets; each entry's observed engine_stream_key
 * now lets the overlay reconcile comparable claims across these buckets.
 *
 * Superseded revisions, appended-but-unselected revisions and null-range
 * (fallback) rows own no interval and are absent. Memoised on the three
 * ledger branches it reads, so it is reference-stable across unrelated
 * actions.
 */
export const selectSettledEngineRanges = createSelector(
  [
    (state: TranslationRootState) => state.translation.revisions,
    (state: TranslationRootState) => state.translation.selections,
    (state: TranslationRootState) => state.translation.turns,
  ],
  (
    revisions: TranslationState['revisions'],
    selections: TranslationState['selections'],
    turns: TranslationState['turns'],
  ): ReadonlyMap<string, readonly SettledEngineRange[]> => {
    const registry = new Map<string, SettledEngineRange[]>();
    for (const [key, selection] of Object.entries(selections)) {
      const selected = revisions[key]?.find(
        (entry) => entry.translation_revision_id === selection.selected_revision_id,
      );
      if (!selected) continue;
      const attachment = revisionAttachmentOf(selected);
      if (
        attachment === null ||
        attachment.engine_range_start === null ||
        attachment.engine_range_end === null
      ) {
        continue;
      }
      const turn = turns[selected.source_turn_id];
      const space = engineCoordinateSpaceKey({
        allocation_group_id: attachment.allocation_group_id,
        target_language: selected.target_language,
        stable_participant_id: turn?.stable_participant_id ?? null,
        source_epoch: turn?.source_epoch ?? null,
      });
      const entry: SettledEngineRange = {
        source_turn_id: selected.source_turn_id,
        target_language: selected.target_language,
        translation_revision_id: selected.translation_revision_id,
        revision_index: selected.revision_index,
        allocation_group_id: attachment.allocation_group_id,
        start: attachment.engine_range_start,
        end: attachment.engine_range_end,
        text: selected.text,
      };
      const streamKey = captionEngineStreamKey({
        call_id: selected.call_id,
        stable_participant_id: turn?.stable_participant_id ?? null,
        source_epoch: turn?.source_epoch ?? null,
        source_track_sid: turn?.source_track_sid ?? null,
        target_language: selected.target_language,
        assignment_generation: selected.assignment_generation,
        route_generation: selected.route_generation,
      });
      const scopedEntry = streamKey === null ? entry : { ...entry, engine_stream_key: streamKey };
      const group = registry.get(space);
      if (group) group.push(scopedEntry);
      else registry.set(space, [scopedEntry]);
    }
    for (const group of registry.values()) {
      group.sort(
        (left, right) =>
          left.start - right.start ||
          left.end - right.end ||
          (left.source_turn_id < right.source_turn_id ? -1 : left.source_turn_id > right.source_turn_id ? 1 : 0),
      );
    }
    return registry;
  },
);

/**
 * The merged record for one (turn, language) pair, or `null` for an unadmitted
 * turn. Plain functions, not memoised: they are read per turn by the tile's
 * bracket and by tests, and the composition is a string join.
 */
export const selectMergedRecord = (
  state: TranslationRootState,
  sourceTurnId: string,
  targetLanguage: LaunchLanguageCode,
): TranslationMergedRecord | null => {
  const translation = state.translation;
  const turn = translation.turns[sourceTurnId];
  if (!turn) return null;
  const key = pairKey(sourceTurnId, targetLanguage);
  const selection = translation.selections[key];
  const selected = selection
    ? (translation.revisions[key] ?? []).find(
        (entry) => entry.translation_revision_id === selection.selected_revision_id,
      ) ?? null
    : null;
  // The transcript is the source text's ONLY home; a store without that slice
  // (a translation-only harness) simply has no source text in view.
  const messages = state.transcription?.transcripts ?? [];
  return mergedRecordFor(
    translation,
    turn,
    targetLanguage,
    selected,
    transcriptSourceTextForTurn(translation, messages, sourceTurnId),
  );
};

/** The origin of the pair's selected revision — `null` when unsettled or unsaid. */
export const selectTranslationTurnOrigin = (
  state: TranslationRootState,
  sourceTurnId: string,
  targetLanguage: LaunchLanguageCode,
): TranslationOrigin | null => selectMergedRecord(state, sourceTurnId, targetLanguage)?.origin ?? null;

/**
 * The join. One view per bubble that has something to say about its translation.
 *
 * Reading this selector cannot change a source bubble: it takes
 * `selectTranscriptionBubbles` as an input and never writes.
 */
export const selectBubbleTranslations = createSelector(
  [selectTranscriptionBubbles, selectTranslationSlice],
  (
    bubbles: TranscriptionBubble[],
    translation: TranslationState,
  ): ReadonlyMap<string, BubbleTranslationView> => {
    const views = new Map<string, BubbleTranslationView>();
    // The reader's language, not just the explicit text pick: the same
    // three-way coalesce selectCaptionReaderLanguage and selectCaptionRelations
    // defend. A reader whose only language is the server-assigned listen
    // language (the /dispatch surface never runs the pre-join commit) must be
    // served the same views the caption regime armed itself for — gating the
    // join on the raw pick alone left the regime active over a permanently
    // empty map, rendered as "Caption translation unavailable" on every
    // foreign line (fallback-audit #7).
    const targetLanguage =
      translation.targetLanguage ??
      translation.viewerListenLanguage ??
      translation.captionListenLanguageMemory;
    // "Not translating" and "translating, but not here yet" are different
    // things, and the difference is a route fact rather than a guess. Durable
    // selected text is different again: once authenticated and selected for
    // this call it remains readable through a transient viewer-context renewal.
    // The live route gate may create waiting views; it may never erase text the
    // reader was already shown.
    if (targetLanguage === null) return views;
    const displayActive = isTranslationDisplayActive(translation);
    for (const bubble of bubbles) {
      const view = buildBubbleTranslationView(translation, bubble, targetLanguage);
      if (view && (displayActive || view.text !== null)) views.set(bubble.bubbleId, view);
    }
    return views;
  },
);

/**
 * A pinned quote rendered at its CURRENT revisions, flagged when they moved.
 *
 * Takes the slice rather than the root state, and returns a fresh object, so a
 * consumer must memoise on `state.translation` (which is reference-stable
 * between translation actions) rather than calling it inside `useSelector`,
 * where a new object every time would re-render on every store action.
 */
export function buildTranslationQuoteView(
  translation: TranslationState,
  quoteId: string,
): TranslationQuoteView | null {
  const quote = translation.quotes[quoteId];
  if (!quote) return null;

  // REVALIDATE the lineage this quote was built on, every time it is rendered.
  // A correlation contradicted after the pin makes its key permanently
  // ambiguous; one that now names a different turn has been superseded. Either
  // way what the quote relied on is no longer true, and an evidence surface must
  // stop showing it rather than keep presenting a disproven translation.
  const lineageStillHolds =
    quote.pinned_lineage.length > 0 &&
    quote.pinned_lineage.every(
      (entry) =>
        !translation.ambiguousTranscriptSyncIds[entry.transcript_sync_id] &&
        translation.sourceTurnIdByTranscriptSyncId[entry.transcript_sync_id] ===
          entry.source_turn_id,
    );

  if (!lineageStillHolds) {
    return {
      quote_id: quote.quote_id,
      scope: quote.scope,
      target_language: quote.target_language,
      turns: [],
      content: 'awaiting_lineage',
      text: null,
      changed_since_pin: false,
      changed_turn_count: 0,
      badges: ['translating'],
      covers_whole_turn: quote.scope === 'span',
      lineage_complete: false,
    };
  }

  const turns: TranslationTurnView[] = [];
  let changedTurnCount = 0;
  for (const sourceTurnId of quote.source_turn_ids) {
    const view = buildTurnView(translation, sourceTurnId, quote.target_language);
    if (!view) continue;
    turns.push(view);
    // Compare the MONOTONIC change count, not the current id. An id comparison
    // reports "unchanged" after the selection moved away and came back, quietly
    // un-flagging a quote whose wording the reader saw change twice.
    const selection = translation.selections[pairKey(sourceTurnId, quote.target_language)];
    const currentCount = selection ? selection.selection_change_count : 0;
    const pinnedCount = quote.pinned_selection_change_counts[sourceTurnId] ?? 0;
    // The id comparison is still consulted, because text appearing where a quote
    // had none is also a change to what the reader is looking at.
    const idMoved =
      view.translation_revision_id !== (quote.pinned_revision_ids[sourceTurnId] ?? null);
    if (currentCount > pinnedCount || idMoved) {
      changedTurnCount += 1;
    }
  }

  // A turn already spoken in the target language is owed no translation, so it
  // contributes neither text nor a badge to the quote.
  const owed = turns.filter((turn) => turn.content !== 'source_is_target');
  const texts = owed
    .map((turn) => turn.text)
    .filter((text): text is string => text !== null && text.length > 0);

  return {
    quote_id: quote.quote_id,
    scope: quote.scope,
    target_language: quote.target_language,
    turns,
    content: owed.length === 0 ? 'source_is_target' : summariseContent(owed),
    text: texts.length > 0 ? texts.join(' ') : null,
    changed_since_pin: changedTurnCount > 0,
    changed_turn_count: changedTurnCount,
    badges: reduceBadges(owed),
    covers_whole_turn: quote.scope === 'span',
    lineage_complete: true,
  };
}

/** Root-state convenience wrapper. See {@link buildTranslationQuoteView}. */
export function selectTranslationQuoteView(
  state: TranslationRootState,
  quoteId: string,
): TranslationQuoteView | null {
  return buildTranslationQuoteView(state.translation, quoteId);
}

// ---------------------------------------------------------------------------
// verbatim quotes inside officer-facing prose
// ---------------------------------------------------------------------------

/**
 * Owner, 2026-08-26, on the consent-evidence line reading
 * `Caller agreed "可以了,没问题"`: "for the most part this is fine, however, when
 * showing original language, we should always provide the officers chosen
 * language translation in brackets next to it."
 *
 * These helpers serve the evidence surfaces that embed a participant's words
 * VERBATIM in prose the officer reads — consent evidence, answered-part
 * quotes, AI evidence excerpts. They are not the caption/transcript tiles,
 * which have their own display modes and their own lineage.
 *
 * A quote arrives as bare text: the detectors that produce it quote the
 * transcript, they do not carry its identity. So the bracket is only ever
 * earned by joining that text back to ONE source turn and reading the
 * revision the viewer's language already selected. Three refusals, all of
 * which leave the quote exactly as it is today:
 *
 *   • the text does not resolve to exactly one source turn — a translation
 *     stitched from an ambiguous join is a claim about who said what;
 *   • the turn was spoken in the viewer's own language, or in no language the
 *     platform knows — "we do not know" is not "it differs";
 *   • no revision in the viewer's language is SELECTED for that turn.
 *
 * Nothing here translates. There is no fallback that machine-translates on the
 * fly, and no path that renders an appended-but-unselected revision.
 */

/** Whitespace-insensitive comparison form. Never a display form. */
function normaliseQuoteText(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

/**
 * The single source turn a verbatim quote came from, or null when the join is
 * absent or ambiguous.
 *
 * Two tiers, tried in order, each resolving only on a UNIQUE turn:
 *   1. a final message whose text IS the quote;
 *   2. a final message whose text CONTAINS the quote — the detector quoted a
 *      fragment, or the message carries trailing punctuation the quote dropped.
 *
 * The converse (a quote spanning several messages) is deliberately not a tier:
 * the quote would then be covered by several turns' revisions and nothing here
 * knows how to apportion them, so it stays untouched.
 */
export function resolveVerbatimQuoteSourceTurnId(
  translation: TranslationState,
  transcripts: readonly TranscriptionMessage[],
  quote: string,
): string | null {
  const needle = normaliseQuoteText(quote);
  if (needle.length === 0) return null;

  const exact = new Set<string>();
  const containing = new Set<string>();
  for (const message of transcripts) {
    // Interims are still being revised, and a locally-derived identity is not
    // a lineage key — neither can carry a quote back to a turn.
    if (message.isFinal !== true) continue;
    if (!isJoinableTranscriptIdentity(message.syncId)) continue;
    if (translation.ambiguousTranscriptSyncIds[message.syncId]) continue;
    const sourceTurnId = translation.sourceTurnIdByTranscriptSyncId[message.syncId];
    if (!sourceTurnId) continue;

    const haystack = normaliseQuoteText(message.message ?? '');
    if (haystack.length === 0) continue;
    if (haystack === needle) exact.add(sourceTurnId);
    else if (haystack.includes(needle)) containing.add(sourceTurnId);
  }

  for (const tier of [exact, containing]) {
    if (tier.size === 1) return [...tier][0];
    if (tier.size > 1) return null;
  }
  return null;
}

/**
 * The reader-language text a verbatim quote is owed, or null when it is owed
 * none. See the block comment above for every refusal.
 */
export function resolveVerbatimQuoteTranslation(
  translation: TranslationState,
  transcripts: readonly TranscriptionMessage[],
  quote: string | null | undefined,
  readerLanguage: LaunchLanguageCode | null,
): string | null {
  if (!quote || readerLanguage === null) return null;

  const sourceTurnId = resolveVerbatimQuoteSourceTurnId(translation, transcripts, quote);
  if (sourceTurnId === null) return null;

  // EXPLICIT, rather than leaning on `content !== 'source_is_target'`: that
  // status is false for an UNKNOWN source language too, and an unknown
  // language has not been shown to differ from the reader's.
  const turn = translation.turns[sourceTurnId];
  if (!turn || turn.source_language === null) return null;
  if (languageEquals(turn.source_language, readerLanguage)) return null;

  const view = buildTurnView(translation, sourceTurnId, readerLanguage);
  if (!view || view.content !== 'translated') return null;
  const text = view.text === null ? '' : view.text.trim();
  return text.length > 0 ? text : null;
}

/**
 * The quote as the officer should read it: its own words, then the reader's
 * language in brackets when — and only when — one has been earned.
 */
export function bracketVerbatimQuote(
  translation: TranslationState,
  transcripts: readonly TranscriptionMessage[],
  quote: string,
  readerLanguage: LaunchLanguageCode | null,
): string {
  const translated = resolveVerbatimQuoteTranslation(
    translation,
    transcripts,
    quote,
    readerLanguage,
  );
  return translated === null ? quote : `${quote} (${translated})`;
}
